import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";

import {
  deleteE2EAgent,
  deleteE2EAgentJson,
  e2eAgentJson,
  e2eAgentResponse,
  patchE2EAgentJson,
  postE2EAgentJson,
  putE2EAgentUpload,
} from "../../api/transport";
import {
  calculateXLMFormulaValues,
  carryXLMFormulaEvaluation,
  prepareXLMFormulaEvaluation,
} from "../lib/xlmFormulaEvaluation";

export type AgentInputMode = "research" | "canalyst";
export type AgentRunMode = "standard" | "fast_track";
export type AgentVisibility = "private" | "public";
export type AgentRunCatalogStatus =
  | "draft"
  | "queued"
  | "active"
  | "succeeded"
  | "failed"
  | "canceled"
  | "expired";
export type AgentPhase =
  | "preparing"
  | "sandboxing"
  | "certifying"
  | "publishing";
export type AgentOutcome = "succeeded" | "failed" | "canceled" | "expired";

export const DEFAULT_AGENT_START_DATE = "2005-01-01";

export interface AgentRunParameters {
  run_mode: AgentRunMode;
  input_mode: AgentInputMode;
  model: string;
  reasoning_effort?: string;
  fast: boolean;
  timeout_seconds: number;
  start_date?: string;
}

export type AgentRunState =
  | {
      kind: "draft";
      input_readiness: "incomplete" | "ready";
      expires_at: string;
      missing_roles: "canalyst"[];
    }
  | { kind: "queued"; attempt: number }
  | { kind: "active"; phase: AgentPhase; attempt: number }
  | {
      kind: "terminal";
      outcome: AgentOutcome;
      failure: {
        phase: AgentPhase;
        code: string;
        category: string;
        retryable: boolean;
        safe_message: string;
      } | null;
    };

export interface AgentArtifact {
  role: string;
  filename: string;
  media_type: string;
  size_bytes: number;
  sha256: string;
  visibility: "launcher";
}

export interface AgentAttribution {
  subject: string;
  email: string | null;
  display_name: string | null;
  channel: "browser" | "cli" | "mcp" | "api";
  credential_name: string | null;
}

export interface AgentVersion {
  id: string;
  version: string;
  source_repository: string;
  source_commit_sha: string;
  source_commit_url: string;
  source_commit_ref: string | null;
  source_commit_message: string;
  source_commit_author_name: string | null;
  source_commit_authored_at: string | null;
  source_pull_request_number: number | null;
  source_pull_request_title: string | null;
  source_pull_request_url: string | null;
  published_at: string;
}

export interface AgentBaseModelReference {
  model_version_id: string;
  run_id: string;
  sequence: number;
}

export interface AgentRun {
  id: string;
  company_id: string;
  agent_version?: AgentVersion | null;
  base_model: AgentBaseModelReference | null;
  ticker: string;
  company_name: string;
  country: string;
  visibility: AgentVisibility;
  hypothesis: string;
  iteration_message: string | null;
  parameters: AgentRunParameters;
  correlation: Record<string, string>;
  state: AgentRunState;
  state_version: number;
  workflow_id: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  artifacts: AgentArtifact[];
  inputs: AgentRunInput[];
  model_version_id: string | null;
  started_by: AgentAttribution | null;
}

export interface AgentRunInput {
  id: string;
  role: "canalyst" | "other_document";
  filename: string;
  media_type: string;
  size_bytes: number;
  sha256: string;
  source_url: string | null;
  description: string | null;
  release_date: string | null;
}

export interface AgentCompany {
  id: string;
  ticker: string;
  company_name: string;
  country: string;
  created_at: string;
  updated_at: string;
}

export interface AgentCompanyCatalogQuery {
  search?: string;
  country?: string;
  hasModel?: boolean;
  sortBy?: "name" | "release_date";
  sortOrder?: "asc" | "desc";
}

export interface AgentRunCatalogQuery {
  createdBy?: string;
}

export interface AgentRunPageQuery {
  companyId?: string;
  createdBy?: string;
  status?: AgentRunCatalogStatus;
  tickerSearch?: string;
  offset?: number;
}

export interface AgentRunPage {
  items: AgentRun[];
  total: number;
  limit: number;
  offset: number;
}

export interface AgentDocumentCatalogQuery {
  search?: string;
  documentType?: "agent_fetched" | "analyst_upload" | "tucker_document";
  runId?: string;
  sortBy?: "name" | "release_date";
  sortOrder?: "asc" | "desc";
}

const AGENT_DOCUMENT_HIERARCHIES = [
  "regulatory",
  "company_disclosure",
  "approved_structured_provider",
  "trusted_industry",
  "approved_sell_side",
  "broader_web",
] as const;

export type AgentDocumentHierarchy =
  (typeof AGENT_DOCUMENT_HIERARCHIES)[number];

function isAgentDocumentHierarchy(
  value: string,
): value is AgentDocumentHierarchy {
  return (AGENT_DOCUMENT_HIERARCHIES as readonly string[]).includes(value);
}

export interface AgentStoredDocument {
  id: string;
  company_id: string;
  type: "agent_fetched" | "analyst_upload" | "tucker_document";
  source_type: "agent_fetched" | "analyst_upload" | "tucker_document";
  name: string;
  sha256: string;
  source_url: string | null;
  description: string;
  release_date: string | null;
  hierarchy: AgentDocumentHierarchy | null;
  filename: string;
  media_type: string;
  size_bytes: number;
  run_ids: string[];
  created_at: string;
  updated_at: string;
}

export type AgentDocument = AgentStoredDocument;

export interface AgentModel {
  id: string;
  url?: string | null;
  company_id: string;
  ticker: string;
  company_name: string;
  country: string;
  run_id: string;
  visibility: AgentVisibility;
  sequence: number;
  published_at: string;
  hypothesis: string;
  iteration_message: string | null;
  input_mode: AgentInputMode;
  model: string;
  fast: boolean;
  reporting_currency: string | null;
  ipo_date: string | null;
  latest_reporting_period: string | null;
  artifacts?: AgentArtifact[];
  generated_by: AgentAttribution | null;
  base_model: AgentBaseModelReference | null;
}

export type AgentChatTurnState = "queued" | "running" | "succeeded" | "failed";
export type AgentChatTurnMode = "ask" | "edit" | "edit_autoapprove";
export type AgentChatEditSurface = "web" | "excel";
export type AgentChatEditProposalStatus =
  | "pending"
  | "applied"
  | "rejected"
  | "stale"
  | "failed";

export interface AgentChatSnapshot {
  revision: number;
  sha256: string;
  size_bytes: number;
}

export interface AgentChatToolCall {
  sequence: number;
  round: number;
  call_id: string;
  name: string;
  arguments: string;
  output: string | null;
}

export interface AgentChatIntermediateItem {
  sequence: number;
  round: number;
  kind: "reasoning_summary" | "assistant_text";
  text: string;
}

export interface AgentChatEditProposal {
  id: string;
  turn_id: string;
  tool_call_id: string;
  description: string;
  operations: XLMEditOperation[];
  base_revision: number;
  status: AgentChatEditProposalStatus;
  changes: string[];
  finding_count: number;
  applied_revision: number | null;
  safe_message: string | null;
  created_at: string;
  decided_at: string | null;
}

export interface AgentChatEditDecision {
  proposal: AgentChatEditProposal;
  mutation: XLMRevisionMutation | null;
}

export interface AgentChatEditRejection {
  proposal: AgentChatEditProposal;
  turn: AgentChatTurn;
}

export interface AgentChatTurn {
  id: string;
  thread_id: string;
  sequence: number;
  state: AgentChatTurnState;
  mode: AgentChatTurnMode;
  edit_surface: AgentChatEditSurface;
  input_snapshot: AgentChatSnapshot;
  result_snapshot: AgentChatSnapshot | null;
  question: string;
  answer_markdown: string | null;
  citations: string[];
  intermediate: AgentChatIntermediateItem[];
  tool_calls: AgentChatToolCall[];
  insufficient_evidence: boolean | null;
  failure_code: string | null;
  safe_message: string | null;
  edit_proposals: AgentChatEditProposal[];
  edit_proposal: AgentChatEditProposal | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface AgentChatThreadListItem {
  id: string;
  company_id: string;
  model_version_id: string;
  model_sequence: number;
  snapshot: AgentChatSnapshot;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentChatThread extends AgentChatThreadListItem {
  turns: AgentChatTurn[];
}

export interface AgentModelPreset {
  id: string;
  label: string;
  supports_fast: boolean;
  reasoning_efforts: string[];
  default_reasoning_effort: string;
}

export interface AgentConfiguration {
  iteration_enabled: boolean;
  start_date_selection_enabled?: boolean;
  default_start_date?: string;
  models: AgentModelPreset[];
  agent_versions?: AgentVersion[];
  default_agent_version?: string | null;
  input_modes: AgentInputMode[];
  default_model: string;
  default_fast: boolean;
  fast_track_defaults: {
    fast: true;
    timeout_seconds: number;
  };
  default_timeout_seconds: number;
  max_timeout_seconds: number;
  max_input_files: number;
  run_draft_ttl_seconds: number;
  supported_extensions: string[];
}

export interface AgentInput {
  id: string;
  company_id: string;
  role: "canalyst" | "other_document";
  filename: string;
  media_type: string;
  size_bytes: number;
  sha256: string;
  source_url: string | null;
  description: string | null;
  release_date: string | null;
  state: "pending" | "ready" | "rejected";
  rejection_reason: string | null;
  created_at: string;
  completed_at: string | null;
  upload_url: string | null;
  upload_headers: Record<string, string>;
}

export interface AgentInputMetadata {
  sourceUrl?: string;
  description?: string;
  releaseDate?: string;
}

export interface XLMValue {
  year: number;
  quarter?: number;
  half?: number;
  value?: number | null;
  formula?: string | null;
  computed_value?: number | boolean | null;
  source?: string | null;
  comment?: string | null;
  document_references?: XLMDocumentReference[];
}

export interface XLMDocumentBoundingBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type XLMDocumentLocator =
  | { kind: "xlsx"; sheet: string; cell_range: string }
  | {
      kind: "pdf";
      page: number;
      table?: string | null;
      bounding_box?: XLMDocumentBoundingBox | null;
    }
  | {
      kind: "pptx";
      slide: number;
      shape?: number | string | null;
      table?: number | string | null;
      row?: number | null;
      column?: number | null;
    }
  | {
      kind: "docx";
      paragraph?: number | null;
      table?: number | string | null;
      row?: number | null;
      column?: number | null;
    }
  | { kind: "csv"; row: number; column: number | string }
  | { kind: "json"; pointer: string }
  | {
      kind: "image";
      page?: number | null;
      bounding_box: XLMDocumentBoundingBox;
    }
  | { kind: "web"; start: number; end: number };

export interface XLMDocumentLocation {
  locator: XLMDocumentLocator;
  excerpt?: string | null;
}

export interface XLMDocumentReference {
  document_id: string;
  locations: XLMDocumentLocation[];
  evidence?: string | null;
}

export type XLMCellCollection =
  | "yearly"
  | "quarterly"
  | "half_year"
  | "yearly_reported_total"
  | "quarterly_reported_total"
  | "half_year_reported_total";

export interface XLMCellAddress {
  identifier: string;
  collection: XLMCellCollection;
  year: number;
  index: number | null;
}

export interface XLMNodeReference {
  identifier: string;
}

export type XLMGuidanceProviderType =
  | "Guidance"
  | "Consensus"
  | "Independent forecast";

export interface XLMGuidanceRecord {
  record_id: string;
  target_ref: XLMCellAddress;
  provider_type: XLMGuidanceProviderType;
  provider: string;
  provider_value: number;
  explanation: string;
  evidence: XLMDocumentReference[];
}

export interface XLMSnapshotIdentity {
  revision: number;
  sha256: string;
  size_bytes: number;
}

export interface XLMEditFinding {
  code: string;
  message: string;
  identifier: string | null;
  period: string | null;
}

export type XLMRevisionKind = "published" | "annotation" | "edit" | "restore";

export interface XLMRevisionSummary {
  revision: number;
  xlm_snapshot: XLMSnapshotIdentity;
  kind: XLMRevisionKind;
  changes: string[];
  finding_count: number;
  model_note: string | null;
  restored_from_revision: number | null;
  created_at: string;
  created_by: string | null;
}

export interface XLMRevision extends XLMRevisionSummary {
  findings: XLMEditFinding[];
}

export interface XLMCellValue {
  value: number | null;
  formula: string | null;
  computed_value: number | boolean | null;
  source: string | null;
  comment: string | null;
  document_references: XLMDocumentReference[];
}

export interface XLMCellPatch {
  cell: XLMCellAddress;
  value: XLMCellValue;
}

export interface XLMRevisionMutation extends XLMRevisionSummary {
  cell_patches: XLMCellPatch[];
  reload_required: boolean;
}

export interface XLMRevisionDelta {
  base_revision: number;
  head_revision: number;
  xlm_snapshot: XLMSnapshotIdentity;
  cell_patches: XLMCellPatch[];
  reload_required: boolean;
}

export interface XLMRevisionList {
  head_revision: number;
  revisions: XLMRevisionSummary[];
}

export interface XLMEditCellOperation {
  kind: "edit_cell";
  cell: XLMCellAddress;
  value?: number | null;
  formula?: string | null;
  availability?: "not_disclosed" | "not_applicable" | "not_researched" | null;
  comment?: string | null;
  document_references?: XLMDocumentReference[];
}

export type XLMNodeType =
  | "truth"
  | "computed"
  | "total"
  | "title"
  | "section_title"
  | "separator";

export interface XLMAddNodeOperation {
  kind: "add_node";
  identifier: string;
  name: string;
  type: XLMNodeType;
  description?: string | null;
  unit?: string | null;
  summable?: boolean;
  parent_breakdown_item_id?: string | null;
  before_identifier?: string | null;
  after_identifier?: string | null;
}

export interface XLMAddBreakdownOperation {
  kind: "add_breakdown";
  identifier: string;
  name: string;
  description?: string | null;
  unit?: string | null;
  render_mix?: boolean;
  render_header?: boolean;
  before_identifier?: string | null;
  after_identifier?: string | null;
}

export interface XLMEditNodeOperation {
  kind: "edit_node";
  identifier: string;
  name?: string;
  type?: XLMNodeType;
  description?: string | null;
  unit?: string | null;
  summable?: boolean;
  parent_breakdown_item_id?: string | null;
  render_total?: boolean;
  render_growth?: boolean;
}

export interface XLMMoveNodeOperation {
  kind: "move_node";
  identifier: string;
  scope?: "header_only" | "group" | null;
  before_identifier?: string | null;
  after_identifier?: string | null;
}

export interface XLMDeleteNodeOperation {
  kind: "delete_node";
  identifier: string;
  scope?: "header_only" | "group" | null;
}

export interface XLMAddBreakdownItemOperation {
  kind: "add_breakdown_item";
  breakdown_identifier: string;
  identifier: string;
  name: string;
  description?: string | null;
  before_identifier?: string | null;
  after_identifier?: string | null;
}

export interface XLMEditBreakdownItemOperation {
  kind: "edit_breakdown_item";
  breakdown_identifier: string;
  identifier: string;
  name?: string;
  description?: string | null;
}

export interface XLMMoveBreakdownItemOperation {
  kind: "move_breakdown_item";
  breakdown_identifier: string;
  identifier: string;
  before_identifier?: string | null;
  after_identifier?: string | null;
}

export interface XLMDeleteBreakdownItemOperation {
  kind: "delete_breakdown_item";
  breakdown_identifier: string;
  identifier: string;
}

export type XLMEditOperation =
  | XLMEditCellOperation
  | XLMAddNodeOperation
  | XLMAddBreakdownOperation
  | XLMEditNodeOperation
  | XLMMoveNodeOperation
  | XLMDeleteNodeOperation
  | XLMAddBreakdownItemOperation
  | XLMEditBreakdownItemOperation
  | XLMMoveBreakdownItemOperation
  | XLMDeleteBreakdownItemOperation;

export interface ModelAnnotationState {
  revision: number;
  xlm_snapshot: XLMSnapshotIdentity;
  model_note: string | null;
  edited_at: string | null;
  edited_by: string | null;
  source_compliant: boolean;
}

export interface XLMNode {
  identifier: string;
  name: string;
  description?: string | null;
  type: XLMNodeType;
  unit?: string | null;
  summable?: boolean;
  yearly_values?: XLMValue[] | null;
  quarterly_values?: XLMValue[] | null;
  half_year_values?: XLMValue[] | null;
  parent_breakdown_item_id?: string | null;
  excel_row?: number | null;
  line_item_id?: string | null;
  unit_original?: string | null;
  breakdown?: {
    unit?: string | null;
    items: XLMNode[];
    render_mix?: boolean;
    render_total?: boolean;
    render_growth?: boolean;
    render_header?: boolean;
    yearly_reported_totals?: XLMValue[] | null;
    quarterly_reported_totals?: XLMValue[] | null;
    half_year_reported_totals?: XLMValue[] | null;
  } | null;
}

export interface XLMModel {
  nodes: XLMNode[];
  guidance: XLMGuidanceRecord[];
  fiscal_years: { year: number; ending_date?: string | null }[];
  quarters: {
    year: number;
    quarter: number;
    ending_date?: string | null;
  }[];
  half_years: { year: number; half: number; ending_date?: string | null }[];
  last_historical_period?: {
    year: number;
    quarter: number;
  } | null;
  reporting_currency?: string | null;
  ticker?: string | null;
  company_name?: string | null;
  country_code?: string | null;
}

export interface XLMWorkbookSnapshot {
  model: XLMModel;
  xlm_snapshot: XLMSnapshotIdentity;
}

export interface AgentCompanyRequest {
  ticker: string;
  company_name: string;
  country: string;
}

export interface AgentRunRequest {
  company_id: string;
  visibility: AgentVisibility;
  agent_version?: string | null;
  base_model_version_id?: string | null;
  hypothesis?: string;
  iteration_message?: string | null;
  parameters: AgentRunParameters;
  canalyst_input_id: string | null;
  other_input_ids: string[];
  correlation: Record<string, string>;
}

export function agentRunRetryRequest(run: AgentRun): AgentRunRequest {
  const canalystInput = run.inputs.find((input) => input.role === "canalyst");
  return {
    company_id: run.company_id,
    visibility: run.visibility,
    ...(run.base_model
      ? { base_model_version_id: run.base_model.model_version_id }
      : {}),
    hypothesis: run.hypothesis,
    ...(run.iteration_message
      ? { iteration_message: run.iteration_message }
      : {}),
    parameters: { ...run.parameters },
    canalyst_input_id: canalystInput?.id ?? null,
    other_input_ids: run.inputs
      .filter((input) => input.role === "other_document")
      .map((input) => input.id),
    correlation: { source: "browser", retry_of: run.id },
  };
}

function optionalArray(
  value: Record<string, unknown>,
  key: string,
  label: string,
): unknown[] {
  if (value[key] === undefined) return [];
  return array(value[key], `${label} ${key}`);
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Invalid ${label} response.`);
  }
  return value as Record<string, unknown>;
}

function array(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`Invalid ${label} response.`);
  return value;
}

function requiredString(
  value: Record<string, unknown>,
  key: string,
  label: string,
): string {
  if (typeof value[key] !== "string") {
    throw new Error(`Invalid ${label} response: ${key} is missing.`);
  }
  return value[key];
}

function nullableString(
  value: Record<string, unknown>,
  key: string,
  label: string,
): string | null {
  if (value[key] === null) return null;
  return requiredString(value, key, label);
}

function optionalNullableString(
  value: Record<string, unknown>,
  key: string,
  label: string,
): string | null {
  if (value[key] === undefined) return null;
  return nullableString(value, key, label);
}

function requiredNumber(
  value: Record<string, unknown>,
  key: string,
  label: string,
): number {
  if (typeof value[key] !== "number" || !Number.isFinite(value[key])) {
    throw new Error(`Invalid ${label} response: ${key} is missing.`);
  }
  return value[key];
}

function requiredDecimalNumber(
  value: Record<string, unknown>,
  key: string,
  label: string,
): number {
  const candidate = value[key];
  if (typeof candidate === "number" && Number.isFinite(candidate)) {
    return candidate;
  }
  if (typeof candidate === "string" && candidate.trim() !== "") {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  throw new Error(`Invalid ${label} response: ${key} is missing.`);
}

function requiredBoolean(
  value: Record<string, unknown>,
  key: string,
  label: string,
): boolean {
  if (typeof value[key] !== "boolean") {
    throw new Error(`Invalid ${label} response: ${key} is missing.`);
  }
  return value[key];
}

function optionalNullableNumber(
  value: Record<string, unknown>,
  key: string,
  label: string,
): number | null {
  if (value[key] === undefined || value[key] === null) return null;
  return requiredNumber(value, key, label);
}

function requiredNonnegativeInteger(
  value: Record<string, unknown>,
  key: string,
  label: string,
): number {
  const number = requiredNumber(value, key, label);
  if (!Number.isInteger(number) || number < 0) {
    throw new Error(`Invalid ${label} response: ${key} is not an integer.`);
  }
  return number;
}

function stringRecord(value: unknown, label: string): Record<string, string> {
  const item = record(value, label);
  if (Object.values(item).some((entry) => typeof entry !== "string")) {
    throw new Error(`Invalid ${label} response.`);
  }
  return item as Record<string, string>;
}

function stringArray(value: unknown, label: string): string[] {
  const items = array(value, label);
  if (items.some((entry) => typeof entry !== "string")) {
    throw new Error(`Invalid ${label}.`);
  }
  return items as string[];
}

function normalizeParameters(value: unknown): AgentRunParameters {
  const item = record(value, "agent run parameters");
  const startDate =
    item.start_date === undefined
      ? undefined
      : requiredString(item, "start_date", "agent run parameters");
  if (
    startDate !== undefined &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) ||
      !Number.isFinite(Date.parse(`${startDate}T00:00:00Z`)) ||
      new Date(`${startDate}T00:00:00Z`).toISOString().slice(0, 10) !==
        startDate)
  ) {
    throw new Error("Invalid agent start date.");
  }
  const inputMode = requiredString(item, "input_mode", "agent run parameters");
  const model = requiredString(item, "model", "agent run parameters");
  const reasoningEffort =
    item.reasoning_effort === undefined
      ? undefined
      : requiredString(item, "reasoning_effort", "agent run parameters");
  const runMode = item.run_mode ?? "standard";
  if (!(["research", "canalyst"] as string[]).includes(inputMode)) {
    throw new Error("Invalid agent input mode.");
  }
  if (
    !(runMode === "standard" || runMode === "fast_track") ||
    model.trim() === "" ||
    (reasoningEffort !== undefined && reasoningEffort.trim() === "") ||
    typeof item.fast !== "boolean"
  ) {
    throw new Error("Invalid agent model parameters.");
  }
  return {
    run_mode: runMode,
    input_mode: inputMode as AgentInputMode,
    model,
    ...(reasoningEffort ? { reasoning_effort: reasoningEffort } : {}),
    fast: item.fast,
    ...(startDate !== undefined ? { start_date: startDate } : {}),
    timeout_seconds: requiredNumber(
      item,
      "timeout_seconds",
      "agent run parameters",
    ),
  };
}

function normalizeRunInput(value: unknown): AgentRunInput {
  const item = record(value, "agent run input");
  const role = requiredString(item, "role", "agent run input");
  if (!(role === "canalyst" || role === "other_document")) {
    throw new Error("Invalid agent run input role.");
  }
  return {
    id: requiredString(item, "id", "agent run input"),
    role,
    filename: requiredString(item, "filename", "agent run input"),
    media_type: requiredString(item, "media_type", "agent run input"),
    size_bytes: requiredNumber(item, "size_bytes", "agent run input"),
    sha256: requiredString(item, "sha256", "agent run input"),
    source_url: nullableString(item, "source_url", "agent run input"),
    description: nullableString(item, "description", "agent run input"),
    release_date: nullableString(item, "release_date", "agent run input"),
  };
}

function normalizeState(value: unknown): AgentRunState {
  const item = record(value, "agent run state");
  const kind = requiredString(item, "kind", "agent run state");
  if (kind === "draft") {
    const readiness = requiredString(
      item,
      "input_readiness",
      "agent run state",
    );
    if (!(readiness === "incomplete" || readiness === "ready")) {
      throw new Error("Invalid agent run draft readiness.");
    }
    const missingRoles = array(item.missing_roles, "agent run missing roles");
    if (missingRoles.some((role) => role !== "canalyst")) {
      throw new Error("Invalid agent run missing role.");
    }
    return {
      kind,
      input_readiness: readiness,
      expires_at: requiredString(item, "expires_at", "agent run state"),
      missing_roles: missingRoles as "canalyst"[],
    };
  }
  if (kind === "queued") {
    return {
      kind,
      attempt: requiredNumber(item, "attempt", "agent run state"),
    };
  }
  if (kind === "active") {
    const phase = requiredString(item, "phase", "agent run state");
    if (
      !(
        ["preparing", "sandboxing", "certifying", "publishing"] as string[]
      ).includes(phase)
    ) {
      throw new Error("Invalid agent run phase.");
    }
    return {
      kind,
      phase: phase as AgentPhase,
      attempt: requiredNumber(item, "attempt", "agent run state"),
    };
  }
  if (kind !== "terminal") throw new Error("Invalid agent run state.");
  const outcome = requiredString(item, "outcome", "agent run state");
  if (
    !(["succeeded", "failed", "canceled", "expired"] as string[]).includes(
      outcome,
    )
  ) {
    throw new Error("Invalid agent run outcome.");
  }
  let failure: Extract<AgentRunState, { kind: "terminal" }>["failure"] = null;
  if (item.failure !== null) {
    const raw = record(item.failure, "agent run failure");
    const phase = requiredString(raw, "phase", "agent run failure");
    if (
      !(
        ["preparing", "sandboxing", "certifying", "publishing"] as string[]
      ).includes(phase)
    ) {
      throw new Error("Invalid agent failure phase.");
    }
    if (typeof raw.retryable !== "boolean") {
      throw new Error("Invalid agent failure retryability.");
    }
    failure = {
      phase: phase as AgentPhase,
      code: requiredString(raw, "code", "agent run failure"),
      category: requiredString(raw, "category", "agent run failure"),
      retryable: raw.retryable,
      safe_message: requiredString(raw, "safe_message", "agent run failure"),
    };
  }
  return { kind, outcome: outcome as AgentOutcome, failure };
}

function normalizeArtifact(value: unknown): AgentArtifact | null {
  const item = record(value, "agent artifact");
  const visibility = requiredString(item, "visibility", "agent artifact");
  if (visibility !== "launcher") return null;
  return {
    role: requiredString(item, "role", "agent artifact"),
    filename: requiredString(item, "filename", "agent artifact"),
    media_type: requiredString(item, "media_type", "agent artifact"),
    size_bytes: requiredNumber(item, "size_bytes", "agent artifact"),
    sha256: requiredString(item, "sha256", "agent artifact"),
    visibility,
  };
}

function normalizeAttribution(value: unknown): AgentAttribution | null {
  if (value === null || value === undefined) return null;
  const item = record(value, "agent attribution");
  const channel = requiredString(item, "channel", "agent attribution");
  if (!("browser cli mcp api".split(" ") as string[]).includes(channel)) {
    throw new Error("Invalid agent attribution channel.");
  }
  return {
    subject: requiredString(item, "subject", "agent attribution"),
    email: nullableString(item, "email", "agent attribution"),
    display_name: nullableString(item, "display_name", "agent attribution"),
    channel: channel as AgentAttribution["channel"],
    credential_name: nullableString(
      item,
      "credential_name",
      "agent attribution",
    ),
  };
}

function normalizeAgentVersion(value: unknown): AgentVersion | null {
  if (value === null || value === undefined) return null;
  const item = record(value, "Agent version");
  return {
    id: requiredString(item, "id", "Agent version"),
    version: requiredString(item, "version", "Agent version"),
    source_repository: requiredString(
      item,
      "source_repository",
      "Agent version",
    ),
    source_commit_sha: requiredString(
      item,
      "source_commit_sha",
      "Agent version",
    ),
    source_commit_url: requiredString(
      item,
      "source_commit_url",
      "Agent version",
    ),
    source_commit_ref: nullableString(
      item,
      "source_commit_ref",
      "Agent version",
    ),
    source_commit_message: requiredString(
      item,
      "source_commit_message",
      "Agent version",
    ),
    source_commit_author_name: nullableString(
      item,
      "source_commit_author_name",
      "Agent version",
    ),
    source_commit_authored_at: nullableString(
      item,
      "source_commit_authored_at",
      "Agent version",
    ),
    source_pull_request_number:
      item.source_pull_request_number === null ||
      item.source_pull_request_number === undefined
        ? null
        : requiredNumber(item, "source_pull_request_number", "Agent version"),
    source_pull_request_title: nullableString(
      item,
      "source_pull_request_title",
      "Agent version",
    ),
    source_pull_request_url: nullableString(
      item,
      "source_pull_request_url",
      "Agent version",
    ),
    published_at: requiredString(item, "published_at", "Agent version"),
  };
}

function normalizeBaseModel(value: unknown): AgentBaseModelReference | null {
  if (value === null || value === undefined) return null;
  const item = record(value, "base model reference");
  return {
    model_version_id: requiredString(
      item,
      "model_version_id",
      "base model reference",
    ),
    run_id: requiredString(item, "run_id", "base model reference"),
    sequence: requiredNumber(item, "sequence", "base model reference"),
  };
}

export function normalizeAgentRun(value: unknown): AgentRun {
  const item = record(value, "agent run");
  const visibility = requiredString(item, "visibility", "agent run");
  if (visibility !== "private" && visibility !== "public") {
    throw new Error("Invalid agent run visibility.");
  }
  return {
    id: requiredString(item, "id", "agent run"),
    company_id: requiredString(item, "company_id", "agent run"),
    agent_version: normalizeAgentVersion(item.agent_version),
    base_model: normalizeBaseModel(item.base_model),
    ticker: requiredString(item, "ticker", "agent run"),
    company_name: requiredString(item, "company_name", "agent run"),
    country: requiredString(item, "country", "agent run"),
    visibility,
    hypothesis: requiredString(item, "hypothesis", "agent run"),
    iteration_message: optionalNullableString(
      item,
      "iteration_message",
      "agent run",
    ),
    parameters: normalizeParameters(item.parameters),
    correlation: stringRecord(item.correlation, "agent run correlation"),
    state: normalizeState(item.state),
    state_version: requiredNumber(item, "state_version", "agent run"),
    workflow_id: requiredString(item, "workflow_id", "agent run"),
    created_at: requiredString(item, "created_at", "agent run"),
    started_at: nullableString(item, "started_at", "agent run"),
    finished_at: nullableString(item, "finished_at", "agent run"),
    artifacts: array(item.artifacts, "agent artifacts")
      .map(normalizeArtifact)
      .filter((artifact): artifact is AgentArtifact => artifact !== null),
    inputs: optionalArray(item, "inputs", "agent run inputs").map(
      normalizeRunInput,
    ),
    model_version_id: nullableString(item, "model_version_id", "agent run"),
    started_by: normalizeAttribution(item.started_by),
  };
}

export function normalizeAgentRunPage(value: unknown): AgentRunPage {
  const page = record(value, "agent run page");
  const total = requiredNumber(page, "total", "agent run page");
  const limit = requiredNumber(page, "limit", "agent run page");
  const offset = requiredNumber(page, "offset", "agent run page");
  if (
    !Number.isInteger(total) ||
    total < 0 ||
    !Number.isInteger(limit) ||
    limit < 1 ||
    limit > AGENT_RUN_PAGE_SIZE ||
    !Number.isInteger(offset) ||
    offset < 0
  ) {
    throw new Error("Invalid agent run page bounds.");
  }
  return {
    items: array(page.items, "agent runs").map(normalizeAgentRun),
    total,
    limit,
    offset,
  };
}

function normalizeCompany(value: unknown): AgentCompany {
  const item = record(value, "agent company");
  return {
    id: requiredString(item, "id", "agent company"),
    ticker: requiredString(item, "ticker", "agent company"),
    company_name: requiredString(item, "company_name", "agent company"),
    country: requiredString(item, "country", "agent company"),
    created_at: requiredString(item, "created_at", "agent company"),
    updated_at: requiredString(item, "updated_at", "agent company"),
  };
}

export function normalizeAgentDocument(value: unknown): AgentDocument {
  const item = record(value, "agent document");
  const type = requiredString(item, "type", "agent document");
  const sourceType = requiredString(item, "source_type", "agent document");
  if (
    !(
      sourceType === "agent_fetched" ||
      sourceType === "analyst_upload" ||
      sourceType === "tucker_document"
    )
  ) {
    throw new Error("Invalid agent document source type.");
  }
  if (type !== sourceType) throw new Error("Invalid agent document type.");
  const hierarchy = nullableString(item, "hierarchy", "agent document");
  if (hierarchy !== null && !isAgentDocumentHierarchy(hierarchy)) {
    throw new Error("Invalid agent document hierarchy.");
  }
  return {
    id: requiredString(item, "id", "agent document"),
    company_id: requiredString(item, "company_id", "agent document"),
    type: sourceType,
    source_type: sourceType,
    name: requiredString(item, "name", "agent document"),
    sha256: requiredString(item, "sha256", "agent document"),
    source_url: nullableString(item, "source_url", "agent document"),
    description: requiredString(item, "description", "agent document"),
    release_date: nullableString(item, "release_date", "agent document"),
    hierarchy,
    filename: requiredString(item, "filename", "agent document"),
    media_type: requiredString(item, "media_type", "agent document"),
    size_bytes: requiredNumber(item, "size_bytes", "agent document"),
    run_ids: stringArray(item.run_ids, "agent document run IDs"),
    created_at: requiredString(item, "created_at", "agent document"),
    updated_at: requiredString(item, "updated_at", "agent document"),
  };
}

export function normalizeModel(value: unknown): AgentModel {
  const item = record(value, "agent model");
  const inputMode = requiredString(item, "input_mode", "agent model");
  const visibility =
    item.visibility === undefined
      ? "public"
      : requiredString(item, "visibility", "agent model");
  if (!(["research", "canalyst"] as string[]).includes(inputMode)) {
    throw new Error("Invalid agent model input mode.");
  }
  if (visibility !== "private" && visibility !== "public") {
    throw new Error("Invalid agent model visibility.");
  }
  if (typeof item.fast !== "boolean")
    throw new Error("Invalid agent model tier.");
  return {
    id: requiredString(item, "id", "agent model"),
    url: optionalNullableString(item, "url", "agent model"),
    company_id: requiredString(item, "company_id", "agent model"),
    ticker: requiredString(item, "ticker", "agent model"),
    company_name: requiredString(item, "company_name", "agent model"),
    country: requiredString(item, "country", "agent model"),
    run_id: requiredString(item, "run_id", "agent model"),
    visibility,
    sequence: requiredNumber(item, "sequence", "agent model"),
    published_at: requiredString(item, "published_at", "agent model"),
    hypothesis: requiredString(item, "hypothesis", "agent model"),
    iteration_message: optionalNullableString(
      item,
      "iteration_message",
      "agent model",
    ),
    input_mode: inputMode as AgentInputMode,
    model: requiredString(item, "model", "agent model"),
    fast: item.fast,
    reporting_currency: optionalNullableString(
      item,
      "reporting_currency",
      "agent model",
    ),
    ipo_date: optionalNullableString(item, "ipo_date", "agent model"),
    latest_reporting_period: optionalNullableString(
      item,
      "latest_reporting_period",
      "agent model",
    ),
    generated_by: normalizeAttribution(item.generated_by),
    base_model: normalizeBaseModel(item.base_model),
    ...(Array.isArray(item.artifacts)
      ? {
          artifacts: item.artifacts
            .map(normalizeArtifact)
            .filter((artifact): artifact is AgentArtifact => artifact !== null),
        }
      : {}),
  };
}

function normalizeChatSnapshot(value: unknown): AgentChatSnapshot {
  const item = record(value, "model chat snapshot");
  const revision = requiredNumber(item, "revision", "model chat snapshot");
  const sizeBytes = requiredNumber(item, "size_bytes", "model chat snapshot");
  const sha256 = requiredString(item, "sha256", "model chat snapshot");
  if (
    !Number.isInteger(revision) ||
    revision < 0 ||
    !Number.isInteger(sizeBytes) ||
    sizeBytes < 0 ||
    !/^[0-9a-f]{64}$/u.test(sha256)
  ) {
    throw new Error("Invalid model chat snapshot response.");
  }
  return { revision, sha256, size_bytes: sizeBytes };
}

function normalizeChatToolCall(value: unknown): AgentChatToolCall {
  const item = record(value, "model chat tool call");
  const sequence = requiredNumber(item, "sequence", "model chat tool call");
  const round = requiredNumber(item, "round", "model chat tool call");
  if (
    !Number.isInteger(sequence) ||
    sequence < 1 ||
    !Number.isInteger(round) ||
    round < 1
  ) {
    throw new Error("Invalid model chat tool call response.");
  }
  return {
    sequence,
    round,
    call_id: requiredString(item, "call_id", "model chat tool call"),
    name: requiredString(item, "name", "model chat tool call"),
    arguments: requiredString(item, "arguments", "model chat tool call"),
    output: nullableString(item, "output", "model chat tool call"),
  };
}

function normalizeChatIntermediateItem(
  value: unknown,
): AgentChatIntermediateItem {
  const item = record(value, "model chat intermediate item");
  const sequence = requiredNumber(
    item,
    "sequence",
    "model chat intermediate item",
  );
  const round = requiredNumber(item, "round", "model chat intermediate item");
  const kind = requiredString(item, "kind", "model chat intermediate item");
  if (
    !Number.isInteger(sequence) ||
    sequence < 1 ||
    !Number.isInteger(round) ||
    round < 1 ||
    !(kind === "reasoning_summary" || kind === "assistant_text")
  ) {
    throw new Error("Invalid model chat intermediate item response.");
  }
  return {
    sequence,
    round,
    kind,
    text: requiredString(item, "text", "model chat intermediate item"),
  };
}

function normalizeChatEditProposal(value: unknown): AgentChatEditProposal {
  const item = record(value, "model chat edit proposal");
  const status = requiredString(item, "status", "model chat edit proposal");
  if (
    !(
      ["pending", "applied", "rejected", "stale", "failed"] as string[]
    ).includes(status)
  ) {
    throw new Error("Invalid model chat edit proposal status.");
  }
  const appliedRevision = item.applied_revision;
  if (
    appliedRevision !== null &&
    (typeof appliedRevision !== "number" ||
      !Number.isInteger(appliedRevision) ||
      appliedRevision < 1)
  ) {
    throw new Error("Invalid model chat applied revision.");
  }
  return {
    id: requiredString(item, "id", "model chat edit proposal"),
    turn_id: requiredString(item, "turn_id", "model chat edit proposal"),
    tool_call_id: requiredString(
      item,
      "tool_call_id",
      "model chat edit proposal",
    ),
    description: requiredString(
      item,
      "description",
      "model chat edit proposal",
    ),
    operations: array(
      item.operations,
      "model chat edit proposal operations",
    ) as XLMEditOperation[],
    base_revision: requiredNonnegativeInteger(
      item,
      "base_revision",
      "model chat edit proposal",
    ),
    status: status as AgentChatEditProposalStatus,
    changes: stringArray(item.changes, "model chat edit proposal changes"),
    finding_count: requiredNonnegativeInteger(
      item,
      "finding_count",
      "model chat edit proposal",
    ),
    applied_revision: appliedRevision as number | null,
    safe_message: nullableString(
      item,
      "safe_message",
      "model chat edit proposal",
    ),
    created_at: requiredString(item, "created_at", "model chat edit proposal"),
    decided_at: nullableString(item, "decided_at", "model chat edit proposal"),
  };
}

function normalizeChatEditDecision(value: unknown): AgentChatEditDecision {
  const item = record(value, "model chat edit decision");
  return {
    proposal: normalizeChatEditProposal(item.proposal),
    mutation:
      item.mutation === null || item.mutation === undefined
        ? null
        : normalizeXLMRevisionMutation(item.mutation),
  };
}

function normalizeChatTurn(value: unknown): AgentChatTurn {
  const item = record(value, "model chat turn");
  const state = requiredString(item, "state", "model chat turn");
  if (
    !(["queued", "running", "succeeded", "failed"] as string[]).includes(state)
  ) {
    throw new Error("Invalid model chat turn state.");
  }
  const mode = requiredString(item, "mode", "model chat turn");
  if (!(mode === "ask" || mode === "edit" || mode === "edit_autoapprove")) {
    throw new Error("Invalid model chat turn mode.");
  }
  const editSurface =
    item.edit_surface === undefined
      ? "web"
      : requiredString(item, "edit_surface", "model chat turn");
  if (editSurface !== "web" && editSurface !== "excel") {
    throw new Error("Invalid model chat edit surface.");
  }
  const insufficientEvidence = item.insufficient_evidence;
  if (
    insufficientEvidence !== null &&
    typeof insufficientEvidence !== "boolean"
  ) {
    throw new Error("Invalid model chat evidence response.");
  }
  const legacyEditProposal =
    item.edit_proposal === null || item.edit_proposal === undefined
      ? null
      : normalizeChatEditProposal(item.edit_proposal);
  const editProposals =
    item.edit_proposals === undefined
      ? legacyEditProposal
        ? [legacyEditProposal]
        : []
      : optionalArray(item, "edit_proposals", "model chat turn").map(
          normalizeChatEditProposal,
        );
  return {
    id: requiredString(item, "id", "model chat turn"),
    thread_id: requiredString(item, "thread_id", "model chat turn"),
    sequence: requiredNumber(item, "sequence", "model chat turn"),
    state: state as AgentChatTurnState,
    mode,
    edit_surface: editSurface,
    input_snapshot: normalizeChatSnapshot(item.input_snapshot),
    result_snapshot:
      item.result_snapshot === null
        ? null
        : normalizeChatSnapshot(item.result_snapshot),
    question: requiredString(item, "question", "model chat turn"),
    answer_markdown: nullableString(item, "answer_markdown", "model chat turn"),
    citations: stringArray(item.citations, "model chat citations"),
    intermediate: optionalArray(item, "intermediate", "model chat turn").map(
      normalizeChatIntermediateItem,
    ),
    tool_calls: optionalArray(item, "tool_calls", "model chat turn").map(
      normalizeChatToolCall,
    ),
    insufficient_evidence: insufficientEvidence,
    failure_code: nullableString(item, "failure_code", "model chat turn"),
    safe_message: nullableString(item, "safe_message", "model chat turn"),
    edit_proposals: editProposals,
    edit_proposal:
      legacyEditProposal ?? editProposals[editProposals.length - 1] ?? null,
    created_at: requiredString(item, "created_at", "model chat turn"),
    started_at: nullableString(item, "started_at", "model chat turn"),
    finished_at: nullableString(item, "finished_at", "model chat turn"),
  };
}

function normalizeChatEditRejection(value: unknown): AgentChatEditRejection {
  const item = record(value, "model chat edit rejection");
  return {
    proposal: normalizeChatEditProposal(item),
    turn: normalizeChatTurn(item.turn),
  };
}

function normalizeChatThreadBase(value: unknown): AgentChatThreadListItem {
  const item = record(value, "model chat thread");
  const title = nullableString(item, "title", "model chat thread");
  return {
    id: requiredString(item, "id", "model chat thread"),
    company_id: requiredString(item, "company_id", "model chat thread"),
    model_version_id: requiredString(
      item,
      "model_version_id",
      "model chat thread",
    ),
    model_sequence: requiredNumber(item, "model_sequence", "model chat thread"),
    snapshot: normalizeChatSnapshot(item.snapshot),
    title,
    created_at: requiredString(item, "created_at", "model chat thread"),
    updated_at: requiredString(item, "updated_at", "model chat thread"),
  };
}

export function normalizeAgentChatThreadListItem(
  value: unknown,
): AgentChatThreadListItem {
  return normalizeChatThreadBase(value);
}

export function normalizeAgentChatThread(value: unknown): AgentChatThread {
  const item = record(value, "model chat thread");
  return {
    ...normalizeChatThreadBase(item),
    turns: array(item.turns, "model chat turns").map(normalizeChatTurn),
  };
}

export function normalizeXLMModel(value: unknown): XLMModel {
  const item = record(value, "financial model");
  const nodes = array(item.nodes, "financial model nodes") as XLMNode[];
  return {
    ...(item as unknown as XLMModel),
    nodes,
    guidance: optionalArray(item, "guidance", "financial model").map(
      (value) => {
        const guidanceRecord = record(value, "XLM guidance record");
        return {
          ...(guidanceRecord as unknown as XLMGuidanceRecord),
          provider_value: requiredDecimalNumber(
            guidanceRecord,
            "provider_value",
            "XLM guidance record",
          ),
        };
      },
    ),
    fiscal_years: optionalArray(
      item,
      "fiscal_years",
      "financial model",
    ) as XLMModel["fiscal_years"],
    quarters: optionalArray(
      item,
      "quarters",
      "financial model",
    ) as XLMModel["quarters"],
    half_years: optionalArray(
      item,
      "half_years",
      "financial model",
    ) as XLMModel["half_years"],
  };
}

function normalizeXLMSnapshotIdentity(value: unknown): XLMSnapshotIdentity {
  const item = record(value, "XLM snapshot identity");
  return {
    revision: requiredNonnegativeInteger(
      item,
      "revision",
      "XLM snapshot identity",
    ),
    sha256: requiredString(item, "sha256", "XLM snapshot identity"),
    size_bytes: requiredNonnegativeInteger(
      item,
      "size_bytes",
      "XLM snapshot identity",
    ),
  };
}

function normalizeXLMRevisionKind(value: unknown): XLMRevisionKind {
  if (
    value !== "published" &&
    value !== "annotation" &&
    value !== "edit" &&
    value !== "restore"
  ) {
    throw new Error("Invalid XLM revision kind.");
  }
  return value;
}

function normalizeXLMRevisionSummary(value: unknown): XLMRevisionSummary {
  const item = record(value, "XLM revision");
  const restoredFromRevision = optionalNullableNumber(
    item,
    "restored_from_revision",
    "XLM revision",
  );
  if (
    restoredFromRevision !== null &&
    (!Number.isInteger(restoredFromRevision) || restoredFromRevision < 0)
  ) {
    throw new Error(
      "Invalid XLM revision response: restored_from_revision is not an integer.",
    );
  }
  return {
    revision: requiredNonnegativeInteger(item, "revision", "XLM revision"),
    xlm_snapshot: normalizeXLMSnapshotIdentity(item.xlm_snapshot),
    kind: normalizeXLMRevisionKind(item.kind),
    changes: stringArray(item.changes, "XLM revision changes"),
    finding_count: requiredNonnegativeInteger(
      item,
      "finding_count",
      "XLM revision",
    ),
    model_note: optionalNullableString(item, "model_note", "XLM revision"),
    restored_from_revision: restoredFromRevision,
    created_at: requiredString(item, "created_at", "XLM revision"),
    created_by: optionalNullableString(item, "created_by", "XLM revision"),
  };
}

function normalizeXLMEditFinding(value: unknown): XLMEditFinding {
  const item = record(value, "XLM revision finding");
  return {
    code: requiredString(item, "code", "XLM revision finding"),
    message: requiredString(item, "message", "XLM revision finding"),
    identifier: optionalNullableString(
      item,
      "identifier",
      "XLM revision finding",
    ),
    period: optionalNullableString(item, "period", "XLM revision finding"),
  };
}

export function normalizeXLMRevision(value: unknown): XLMRevision {
  const item = record(value, "XLM revision");
  return {
    ...normalizeXLMRevisionSummary(item),
    findings: array(item.findings, "XLM revision findings").map(
      normalizeXLMEditFinding,
    ),
  };
}

function normalizeXLMCellAddress(value: unknown): XLMCellAddress {
  const item = record(value, "XLM cell address");
  const collection = requiredString(item, "collection", "XLM cell address");
  if (
    collection !== "yearly" &&
    collection !== "quarterly" &&
    collection !== "half_year" &&
    collection !== "yearly_reported_total" &&
    collection !== "quarterly_reported_total" &&
    collection !== "half_year_reported_total"
  ) {
    throw new Error("Invalid XLM cell collection.");
  }
  const year = requiredNumber(item, "year", "XLM cell address");
  const index = optionalNullableNumber(item, "index", "XLM cell address");
  if (
    !Number.isInteger(year) ||
    (index !== null && !Number.isInteger(index)) ||
    ((collection === "yearly" || collection === "yearly_reported_total") &&
      index !== null) ||
    ((collection === "quarterly" ||
      collection === "quarterly_reported_total") &&
      ![1, 2, 3, 4].includes(index ?? 0)) ||
    ((collection === "half_year" ||
      collection === "half_year_reported_total") &&
      ![1, 2].includes(index ?? 0))
  ) {
    throw new Error("Invalid XLM cell period.");
  }
  return {
    identifier: requiredString(item, "identifier", "XLM cell address"),
    collection,
    year,
    index,
  };
}

function normalizeXLMCellValue(value: unknown): XLMCellValue {
  const item = record(value, "XLM cell value");
  const computed = item.computed_value;
  if (
    computed !== null &&
    computed !== undefined &&
    typeof computed !== "number" &&
    typeof computed !== "boolean"
  ) {
    throw new Error("Invalid XLM computed value.");
  }
  return {
    value: optionalNullableNumber(item, "value", "XLM cell value"),
    formula: optionalNullableString(item, "formula", "XLM cell value"),
    computed_value: computed ?? null,
    source: optionalNullableString(item, "source", "XLM cell value"),
    comment: optionalNullableString(item, "comment", "XLM cell value"),
    document_references: array(
      item.document_references,
      "XLM cell document references",
    ) as XLMDocumentReference[],
  };
}

function normalizeXLMCellPatch(value: unknown): XLMCellPatch {
  const item = record(value, "XLM cell patch");
  return {
    cell: normalizeXLMCellAddress(item.cell),
    value: normalizeXLMCellValue(item.value),
  };
}

export function normalizeXLMRevisionMutation(
  value: unknown,
): XLMRevisionMutation {
  const item = record(value, "XLM revision mutation");
  return {
    ...normalizeXLMRevisionSummary(item),
    cell_patches: optionalArray(item, "cell_patches", "XLM revision").map(
      normalizeXLMCellPatch,
    ),
    reload_required:
      item.reload_required === undefined
        ? true
        : requiredBoolean(item, "reload_required", "XLM revision mutation"),
  };
}

export function normalizeXLMRevisionDelta(value: unknown): XLMRevisionDelta {
  const item = record(value, "XLM revision delta");
  return {
    base_revision: requiredNonnegativeInteger(
      item,
      "base_revision",
      "XLM revision delta",
    ),
    head_revision: requiredNonnegativeInteger(
      item,
      "head_revision",
      "XLM revision delta",
    ),
    xlm_snapshot: normalizeXLMSnapshotIdentity(item.xlm_snapshot),
    cell_patches: array(item.cell_patches, "XLM cell patches").map(
      normalizeXLMCellPatch,
    ),
    reload_required: requiredBoolean(
      item,
      "reload_required",
      "XLM revision delta",
    ),
  };
}

function xlmValueMatchesPeriod(value: XLMValue, cell: XLMCellAddress): boolean {
  if (value.year !== cell.year) return false;
  if (
    cell.collection === "quarterly" ||
    cell.collection === "quarterly_reported_total"
  ) {
    return value.quarter === cell.index;
  }
  if (
    cell.collection === "half_year" ||
    cell.collection === "half_year_reported_total"
  ) {
    return value.half === cell.index;
  }
  return cell.index === null;
}

function xlmValuesForCell(
  model: XLMModel,
  cell: XLMCellAddress,
): XLMValue[] | null | undefined {
  const node = findXLMNode(model.nodes, cell.identifier);
  if (cell.collection.endsWith("_reported_total")) {
    const breakdown = node?.breakdown;
    if (cell.collection === "yearly_reported_total") {
      return breakdown?.yearly_reported_totals;
    }
    if (cell.collection === "quarterly_reported_total") {
      return breakdown?.quarterly_reported_totals;
    }
    return breakdown?.half_year_reported_totals;
  }
  if (cell.collection === "yearly") return node?.yearly_values;
  if (cell.collection === "quarterly") return node?.quarterly_values;
  return node?.half_year_values;
}

export function xlmCellAddressKey(cell: XLMCellAddress): string {
  return [
    cell.identifier,
    cell.collection,
    String(cell.year),
    String(cell.index ?? ""),
  ].join(":");
}

export function mergeXLMEditCellOperations(
  current: XLMEditCellOperation,
  next: XLMEditCellOperation,
): XLMEditCellOperation {
  if (xlmCellAddressKey(current.cell) !== xlmCellAddressKey(next.cell)) {
    throw new Error("Only edits for the same XLM cell can be coalesced.");
  }
  return { ...current, ...next, cell: current.cell };
}

function patchedXLMValues(
  values: XLMValue[] | null | undefined,
  patch: XLMCellPatch,
): XLMValue[] | null {
  const current = [...(values ?? [])];
  const matches = current
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => xlmValueMatchesPeriod(value, patch.cell));
  if (matches.length > 1) return null;
  const replacement: XLMValue = {
    year: patch.cell.year,
    ...(patch.cell.collection === "quarterly" ||
    patch.cell.collection === "quarterly_reported_total"
      ? { quarter: patch.cell.index ?? undefined }
      : {}),
    ...(patch.cell.collection === "half_year" ||
    patch.cell.collection === "half_year_reported_total"
      ? { half: patch.cell.index ?? undefined }
      : {}),
    ...patch.value,
  };
  if (matches[0]) {
    current[matches[0].index] = replacement;
  } else {
    current.push(replacement);
  }
  current.sort(
    (left, right) =>
      left.year - right.year ||
      (left.quarter ?? left.half ?? 0) - (right.quarter ?? right.half ?? 0),
  );
  return current;
}

function findXLMNode(
  nodes: XLMNode[],
  identifier: string,
): XLMNode | undefined {
  for (const candidate of nodes) {
    if (candidate.identifier === identifier) return candidate;
    const nested = findXLMNode(candidate.breakdown?.items ?? [], identifier);
    if (nested) return nested;
  }
  return undefined;
}

function patchedXLMNode(node: XLMNode, patch: XLMCellPatch): XLMNode | null {
  if (patch.cell.collection.endsWith("_reported_total")) {
    const breakdown = node.breakdown;
    if (!breakdown) return null;
    if (patch.cell.collection === "yearly_reported_total") {
      const values = patchedXLMValues(breakdown.yearly_reported_totals, patch);
      if (!values) return null;
      return {
        ...node,
        breakdown: { ...breakdown, yearly_reported_totals: values },
      };
    } else if (patch.cell.collection === "quarterly_reported_total") {
      const values = patchedXLMValues(
        breakdown.quarterly_reported_totals,
        patch,
      );
      if (!values) return null;
      return {
        ...node,
        breakdown: { ...breakdown, quarterly_reported_totals: values },
      };
    } else {
      const values = patchedXLMValues(
        breakdown.half_year_reported_totals,
        patch,
      );
      if (!values) return null;
      return {
        ...node,
        breakdown: { ...breakdown, half_year_reported_totals: values },
      };
    }
  }

  if (patch.cell.collection === "yearly") {
    const values = patchedXLMValues(node.yearly_values, patch);
    return values ? { ...node, yearly_values: values } : null;
  } else if (patch.cell.collection === "quarterly") {
    const values = patchedXLMValues(node.quarterly_values, patch);
    return values ? { ...node, quarterly_values: values } : null;
  } else if (patch.cell.collection === "half_year") {
    const values = patchedXLMValues(node.half_year_values, patch);
    return values ? { ...node, half_year_values: values } : null;
  }
  return null;
}

interface PatchedXLMNodes {
  nodes: XLMNode[];
  matched: boolean;
  valid: boolean;
}

function patchedXLMNodes(
  nodes: XLMNode[],
  patch: XLMCellPatch,
): PatchedXLMNodes {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (!node) continue;
    if (node.identifier === patch.cell.identifier) {
      const patched = patchedXLMNode(node, patch);
      if (!patched) return { nodes, matched: true, valid: false };
      const next = [...nodes];
      next[index] = patched;
      return { nodes: next, matched: true, valid: true };
    }
    const items = node.breakdown?.items;
    if (!items) continue;
    const nested = patchedXLMNodes(items, patch);
    if (!nested.matched) continue;
    if (!nested.valid) return { nodes, matched: true, valid: false };
    const next = [...nodes];
    next[index] = {
      ...node,
      breakdown: { ...node.breakdown, items: nested.nodes },
    };
    return { nodes: next, matched: true, valid: true };
  }
  return { nodes, matched: false, valid: false };
}

export function applyXLMCellPatches(
  model: XLMModel,
  patches: XLMCellPatch[],
): XLMModel | null {
  if (patches.length === 0) return model;
  let next = model;
  for (const patch of patches) {
    const current = xlmValuesForCell(next, patch.cell)?.find((value) =>
      xlmValueMatchesPeriod(value, patch.cell),
    );
    const patched = patchedXLMNodes(next.nodes, patch);
    if (!patched.matched || !patched.valid) return null;
    const patchedModel = { ...next, nodes: patched.nodes };
    const currentFormula = current?.formula ?? null;
    carryXLMFormulaEvaluation(
      next,
      patchedModel,
      currentFormula === patch.value.formula
        ? undefined
        : { cell: patch.cell, formula: patch.value.formula },
    );
    next = patchedModel;
  }
  return next;
}

function xlmCellValue(value: XLMValue): XLMCellValue {
  return {
    value: value.value ?? null,
    formula: value.formula ?? null,
    computed_value: value.computed_value ?? null,
    source: value.source ?? null,
    comment: value.comment ?? null,
    document_references: value.document_references ?? [],
  };
}

export function optimisticXLMCellPatches(
  model: XLMModel,
  operation: XLMEditCellOperation,
): XLMCellPatch[] | null {
  const current = xlmValuesForCell(model, operation.cell)?.find((value) =>
    xlmValueMatchesPeriod(value, operation.cell),
  );
  const valueChanged = Object.hasOwn(operation, "value");
  const formulaChanged = Object.hasOwn(operation, "formula");
  const directPatch: XLMCellPatch = {
    cell: operation.cell,
    value: {
      value: valueChanged
        ? (operation.value ?? null)
        : (current?.value ?? null),
      formula: formulaChanged
        ? (operation.formula ?? null)
        : (current?.formula ?? null),
      computed_value: formulaChanged
        ? null
        : valueChanged
          ? (operation.value ?? null)
          : (current?.computed_value ?? current?.value ?? null),
      source: current?.source ?? null,
      comment: Object.hasOwn(operation, "comment")
        ? (operation.comment ?? null)
        : (current?.comment ?? null),
      document_references: Object.hasOwn(operation, "document_references")
        ? (operation.document_references ?? [])
        : (current?.document_references ?? []),
    },
  };
  if (!valueChanged && !formulaChanged) return [directPatch];
  const directlyEdited = applyXLMCellPatches(model, [directPatch]);
  if (!directlyEdited) return null;
  const calculation = calculateXLMFormulaValues(directlyEdited, operation.cell);
  const patches = new Map<string, XLMCellPatch>([
    [xlmCellAddressKey(directPatch.cell), directPatch],
  ]);
  for (const update of calculation.updates) {
    const value = xlmValuesForCell(directlyEdited, update.cell)?.find(
      (candidate) => xlmValueMatchesPeriod(candidate, update.cell),
    );
    if (!value) return null;
    patches.set(xlmCellAddressKey(update.cell), {
      cell: update.cell,
      value: {
        ...xlmCellValue(value),
        computed_value: update.computedValue,
      },
    });
  }
  return [...patches.values()];
}

export function applyOptimisticXLMCellEdit(
  snapshot: XLMWorkbookSnapshot,
  operation: XLMEditCellOperation,
): XLMWorkbookSnapshot | null {
  const patches = optimisticXLMCellPatches(snapshot.model, operation);
  const model = patches ? applyXLMCellPatches(snapshot.model, patches) : null;
  return model ? { ...snapshot, model } : null;
}

function xlmInsertIndex(
  nodes: XLMNode[],
  beforeIdentifier: string | null | undefined,
  afterIdentifier: string | null | undefined,
): number | null {
  if (beforeIdentifier) {
    const index = nodes.findIndex(
      (node) => node.identifier === beforeIdentifier,
    );
    return index < 0 ? null : index;
  }
  if (afterIdentifier) {
    const index = nodes.findIndex(
      (node) => node.identifier === afterIdentifier,
    );
    return index < 0 ? null : index + 1;
  }
  return nodes.length;
}

function xlmRepositionNode(
  nodes: XLMNode[],
  identifier: string,
  beforeIdentifier: string | null | undefined,
  afterIdentifier: string | null | undefined,
): boolean {
  const sourceIndex = nodes.findIndex((node) => node.identifier === identifier);
  const anchor = beforeIdentifier ?? afterIdentifier;
  if (sourceIndex < 0 || !anchor || anchor === identifier) return false;
  const [node] = nodes.splice(sourceIndex, 1);
  if (!node) return false;
  const destination = xlmInsertIndex(nodes, beforeIdentifier, afterIdentifier);
  if (destination === null) return false;
  nodes.splice(destination, 0, node);
  return true;
}

function xlmRepositionNodeGroup(
  nodes: XLMNode[],
  identifier: string,
  beforeIdentifier: string | null | undefined,
  afterIdentifier: string | null | undefined,
): boolean {
  const start = nodes.findIndex((node) => node.identifier === identifier);
  const source = nodes[start];
  if (
    start < 0 ||
    !source ||
    !["section_title", "title"].includes(source.type)
  ) {
    return false;
  }
  let end = start + 1;
  while (end < nodes.length) {
    const candidate = nodes[end];
    if (!candidate || candidate.type === "section_title") break;
    if (source.type === "title" && candidate.type === "title") break;
    end += 1;
  }
  const block = nodes.splice(start, end - start);
  const destination = xlmInsertIndex(nodes, beforeIdentifier, afterIdentifier);
  if (destination === null) return false;
  nodes.splice(destination, 0, ...block);
  return true;
}

export function applyOptimisticXLMStructuralModel(
  source: XLMModel,
  operations: readonly XLMEditOperation[],
): XLMModel | null {
  const model = structuredClone(source);
  for (const operation of operations) {
    if (operation.kind === "edit_cell") return null;
    if (operation.kind === "add_node") {
      if (
        model.nodes.some((node) => node.identifier === operation.identifier)
      ) {
        return null;
      }
      const index = xlmInsertIndex(
        model.nodes,
        operation.before_identifier,
        operation.after_identifier,
      );
      if (index === null) return null;
      model.nodes.splice(index, 0, {
        identifier: operation.identifier,
        name: operation.name,
        type: operation.type,
        description: operation.description ?? null,
        unit: operation.unit ?? null,
        summable: operation.summable ?? true,
      });
      continue;
    }
    if (operation.kind === "add_breakdown") {
      if (
        model.nodes.some((node) => node.identifier === operation.identifier)
      ) {
        return null;
      }
      const index = xlmInsertIndex(
        model.nodes,
        operation.before_identifier,
        operation.after_identifier,
      );
      if (index === null) return null;
      model.nodes.splice(index, 0, {
        identifier: operation.identifier,
        name: operation.name,
        type: "truth",
        description: operation.description ?? null,
        unit: operation.unit ?? null,
        breakdown: {
          unit: operation.unit ?? null,
          items: [],
          render_mix: operation.render_mix ?? false,
          render_total: true,
          render_growth: true,
          render_header: operation.render_header ?? true,
        },
      });
      continue;
    }
    if (operation.kind === "edit_node") {
      const node = model.nodes.find(
        (candidate) => candidate.identifier === operation.identifier,
      );
      if (!node) return null;
      if (Object.hasOwn(operation, "name") && operation.name !== undefined) {
        node.name = operation.name;
      }
      if (Object.hasOwn(operation, "type") && operation.type !== undefined) {
        node.type = operation.type;
      }
      if (Object.hasOwn(operation, "description")) {
        node.description = operation.description ?? null;
      }
      if (Object.hasOwn(operation, "unit")) {
        node.unit = operation.unit ?? null;
      }
      if (Object.hasOwn(operation, "summable")) {
        node.summable = operation.summable;
      }
      if (Object.hasOwn(operation, "render_total")) {
        if (!node.breakdown) return null;
        node.breakdown.render_total = operation.render_total;
      }
      if (Object.hasOwn(operation, "render_growth")) {
        if (!node.breakdown) return null;
        node.breakdown.render_growth = operation.render_growth;
      }
      continue;
    }
    if (operation.kind === "move_node") {
      if (
        !(operation.scope === "group"
          ? xlmRepositionNodeGroup(
              model.nodes,
              operation.identifier,
              operation.before_identifier,
              operation.after_identifier,
            )
          : xlmRepositionNode(
              model.nodes,
              operation.identifier,
              operation.before_identifier,
              operation.after_identifier,
            ))
      ) {
        return null;
      }
      continue;
    }
    if (operation.kind === "delete_node") {
      const index = model.nodes.findIndex(
        (node) => node.identifier === operation.identifier,
      );
      if (index < 0) return null;
      model.nodes.splice(index, 1);
      continue;
    }
    const breakdown = model.nodes.find(
      (node) => node.identifier === operation.breakdown_identifier,
    )?.breakdown;
    if (!breakdown) return null;
    if (operation.kind === "add_breakdown_item") {
      if (
        breakdown.items.some((item) => item.identifier === operation.identifier)
      ) {
        return null;
      }
      const index = xlmInsertIndex(
        breakdown.items,
        operation.before_identifier,
        operation.after_identifier,
      );
      if (index === null) return null;
      breakdown.items.splice(index, 0, {
        identifier: operation.identifier,
        name: operation.name,
        type: "truth",
        description: operation.description ?? null,
        unit: breakdown.unit ?? null,
      });
      continue;
    }
    if (operation.kind === "edit_breakdown_item") {
      const item = breakdown.items.find(
        (candidate) => candidate.identifier === operation.identifier,
      );
      if (!item) return null;
      if (Object.hasOwn(operation, "name") && operation.name !== undefined) {
        item.name = operation.name;
      }
      if (Object.hasOwn(operation, "description")) {
        item.description = operation.description ?? null;
      }
      continue;
    }
    if (operation.kind === "delete_breakdown_item") {
      const index = breakdown.items.findIndex(
        (item) => item.identifier === operation.identifier,
      );
      if (index < 0) return null;
      breakdown.items.splice(index, 1);
      continue;
    }
    if (
      !xlmRepositionNode(
        breakdown.items,
        operation.identifier,
        operation.before_identifier,
        operation.after_identifier,
      )
    ) {
      return null;
    }
  }
  return model;
}

export function applyOptimisticXLMStructuralEdits(
  snapshot: XLMWorkbookSnapshot,
  operations: readonly XLMEditOperation[],
): XLMWorkbookSnapshot | null {
  const model = applyOptimisticXLMStructuralModel(snapshot.model, operations);
  return model ? { ...snapshot, model } : null;
}

export function applyOptimisticXLMEdits(
  snapshot: XLMWorkbookSnapshot,
  operations: readonly XLMEditOperation[],
): XLMWorkbookSnapshot | null {
  let current = snapshot;
  let structural: XLMEditOperation[] = [];
  const flushStructural = (): boolean => {
    if (structural.length === 0) return true;
    const projected = applyOptimisticXLMStructuralEdits(current, structural);
    structural = [];
    if (!projected) return false;
    current = projected;
    return true;
  };
  for (const operation of operations) {
    if (operation.kind !== "edit_cell") {
      structural.push(operation);
      continue;
    }
    if (!flushStructural()) return null;
    const projected = applyOptimisticXLMCellEdit(current, operation);
    if (!projected) return null;
    current = projected;
  }
  return flushStructural() ? current : null;
}

export function applyXLMDelta(
  snapshot: XLMWorkbookSnapshot,
  delta: XLMRevisionDelta,
): XLMWorkbookSnapshot | null {
  if (
    delta.reload_required ||
    delta.base_revision !== snapshot.xlm_snapshot.revision ||
    delta.head_revision !== delta.xlm_snapshot.revision
  ) {
    return null;
  }
  const model = applyXLMCellPatches(snapshot.model, delta.cell_patches);
  return model ? { model, xlm_snapshot: delta.xlm_snapshot } : null;
}

export async function fetchXLMWorkbookSnapshot(
  companyId: string,
  versionId: string,
  signal?: AbortSignal,
): Promise<XLMWorkbookSnapshot> {
  const response = await e2eAgentResponse(
    `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm`,
    signal,
  );
  const model = normalizeXLMModel(await response.json());
  prepareXLMFormulaEvaluation(model);
  const revision = Number(response.headers.get("X-XLM-Revision"));
  const sizeBytes = Number(response.headers.get("X-XLM-Size-Bytes"));
  const sha256 = response.headers.get("X-XLM-Sha256") ?? "";
  if (
    Number.isInteger(revision) &&
    revision >= 0 &&
    Number.isInteger(sizeBytes) &&
    sizeBytes >= 0 &&
    /^[0-9a-f]{64}$/u.test(sha256)
  ) {
    return {
      model,
      xlm_snapshot: {
        revision,
        sha256,
        size_bytes: sizeBytes,
      },
    };
  }

  // During an API-first rolling deployment the previous generation still
  // exposes the verified ETag and revision list, but not the identity headers.
  const etag =
    response.headers.get("ETag")?.replace(/^W\//u, "").replaceAll('"', "") ??
    "";
  const revisions = normalizeXLMRevisionList(
    await e2eAgentJson(
      `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/revisions`,
      signal,
    ),
  );
  const identity = revisions.revisions.find(
    (item) => item.xlm_snapshot.sha256 === etag,
  )?.xlm_snapshot;
  if (!identity)
    throw new Error("The XLM snapshot identity could not be verified.");
  return {
    model,
    xlm_snapshot: identity,
  };
}

export async function fetchXLMWorkbook(
  companyId: string,
  versionId: string,
): Promise<{ content: Blob; filename: string }> {
  const response = await e2eAgentResponse(
    `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/workbook`,
  );
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = /^attachment; filename="([^"/\\]+\.xlsx)"$/u.exec(
    disposition,
  )?.[1];
  if (!filename) throw new Error("The workbook filename was invalid.");
  return { content: await response.blob(), filename };
}

export function normalizeXLMRevisionList(value: unknown): XLMRevisionList {
  const item = record(value, "XLM revision list");
  return {
    head_revision: requiredNonnegativeInteger(
      item,
      "head_revision",
      "XLM revision list",
    ),
    revisions: array(item.revisions, "XLM revisions").map(
      normalizeXLMRevisionSummary,
    ),
  };
}

export const agentQueryKey = (...parts: readonly unknown[]) => [
  "analyst",
  "e2e-agent",
  ...parts,
];

function catalogPath(
  pathname: string,
  values: Record<string, string | boolean | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(values)) {
    if (value === undefined || value === "") continue;
    params.set(name, String(value));
  }
  const query = params.toString();
  return query === "" ? pathname : `${pathname}?${query}`;
}

export function agentCompaniesPath(
  query: AgentCompanyCatalogQuery = {},
): string {
  return catalogPath("/v1/companies", {
    search: query.search?.trim(),
    country: query.country,
    has_model: query.hasModel,
    sort_by: query.sortBy,
    sort_order: query.sortOrder,
  });
}

export function agentRunsPath(query: AgentRunCatalogQuery = {}): string {
  return catalogPath("/v1/runs", {
    created_by: query.createdBy?.trim(),
  });
}

export const AGENT_RUN_PAGE_SIZE = 100;

function normalizedAgentRunPageQuery(
  query: AgentRunPageQuery = {},
): Required<Pick<AgentRunPageQuery, "offset">> &
  Omit<AgentRunPageQuery, "offset"> {
  return {
    companyId: query.companyId?.trim() || undefined,
    createdBy: query.createdBy?.trim() || undefined,
    status: query.status,
    tickerSearch: query.tickerSearch?.trim() || undefined,
    offset: query.offset ?? 0,
  };
}

export function agentRunCatalogPath(query: AgentRunPageQuery = {}): string {
  const normalized = normalizedAgentRunPageQuery(query);
  return catalogPath("/v1/runs/catalog", {
    company_id: normalized.companyId,
    created_by: normalized.createdBy,
    status: normalized.status,
    ticker_search: normalized.tickerSearch,
    limit: String(AGENT_RUN_PAGE_SIZE),
    offset: String(normalized.offset),
  });
}

export function agentRunCatalogQueryKey(query: AgentRunPageQuery = {}) {
  return agentQueryKey("runs", "catalog", normalizedAgentRunPageQuery(query));
}

export function agentDocumentsPath(
  companyId: string,
  query: AgentDocumentCatalogQuery = {},
): string {
  return catalogPath(
    `/v1/companies/${encodeURIComponent(companyId)}/documents`,
    {
      search: query.search?.trim(),
      type: query.documentType,
      run_id: query.runId,
      sort_by: query.sortBy,
      sort_order: query.sortOrder,
      limit: "200",
    },
  );
}

const DOCUMENT_LIST_BATCH_SIZE = 200;

export async function agentDocumentsByIds(
  companyId: string,
  documentIds: string[],
): Promise<AgentDocument[]> {
  const ids = [...new Set(documentIds)].sort();
  const documents: AgentDocument[] = [];
  for (
    let offset = 0;
    offset < ids.length;
    offset += DOCUMENT_LIST_BATCH_SIZE
  ) {
    const batch = ids.slice(offset, offset + DOCUMENT_LIST_BATCH_SIZE);
    const page = record(
      await postE2EAgentJson(
        `/v1/companies/${encodeURIComponent(companyId)}/documents/list`,
        { ids: batch, limit: batch.length },
      ),
      "agent document page",
    );
    documents.push(
      ...array(page.items, "agent documents").map(normalizeAgentDocument),
    );
  }
  return documents;
}

export function useAgentConfiguration() {
  return useQuery({
    queryKey: agentQueryKey("configuration"),
    queryFn: async () =>
      record(
        await e2eAgentJson("/v1/configuration"),
        "agent configuration",
      ) as unknown as AgentConfiguration,
    staleTime: 5 * 60_000,
  });
}

export function useAgentRuns(query: AgentRunCatalogQuery = {}) {
  const createdBy = query.createdBy?.trim() || undefined;
  return useQuery({
    queryKey: createdBy
      ? agentQueryKey("runs", { createdBy })
      : agentQueryKey("runs"),
    placeholderData: (previous) => previous,
    queryFn: async () =>
      array(await e2eAgentJson(agentRunsPath({ createdBy })), "agent runs").map(
        normalizeAgentRun,
      ),
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.state.kind !== "terminal")
        ? 5_000
        : 30_000,
  });
}

export function useAgentRunPage(query: AgentRunPageQuery = {}) {
  const normalized = normalizedAgentRunPageQuery(query);
  return useQuery({
    queryKey: agentRunCatalogQueryKey(normalized),
    placeholderData: (previous) => previous,
    queryFn: async () =>
      normalizeAgentRunPage(
        await e2eAgentJson(agentRunCatalogPath(normalized)),
      ),
    refetchInterval: (catalog) =>
      catalog.state.data?.items.some((run) => run.state.kind !== "terminal")
        ? 5_000
        : 30_000,
  });
}

export function useAgentCompanies(query: AgentCompanyCatalogQuery = {}) {
  return useQuery({
    queryKey: agentQueryKey("companies", query),
    placeholderData: (previous) => previous,
    queryFn: async () =>
      array(
        await e2eAgentJson(agentCompaniesPath(query)),
        "agent companies",
      ).map(normalizeCompany),
  });
}

export function useAgentDocuments(
  companyId: string,
  query: AgentDocumentCatalogQuery = {},
) {
  return useQuery({
    queryKey: agentQueryKey("documents", companyId, query),
    enabled:
      companyId !== "" && (query.runId === undefined || query.runId !== ""),
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const page = record(
        await e2eAgentJson(agentDocumentsPath(companyId, query)),
        "agent document page",
      );
      return array(page.items, "agent documents").map(normalizeAgentDocument);
    },
  });
}

export function useAgentDocumentsByIds(
  companyId: string,
  documentIds: string[],
) {
  const ids = [...new Set(documentIds)].sort();
  return useQuery({
    queryKey: agentQueryKey("documents-by-id", companyId, ids),
    enabled: companyId !== "" && ids.length > 0,
    queryFn: () => agentDocumentsByIds(companyId, ids),
  });
}

export function useAgentRun(runId: string) {
  return useQuery({
    queryKey: agentQueryKey("run", runId),
    enabled: runId !== "",
    queryFn: async () =>
      normalizeAgentRun(await e2eAgentJson(`/v1/runs/${runId}`)),
    refetchInterval: (query) =>
      query.state.data?.state.kind === "terminal" ? false : 5_000,
  });
}

export function useAgentModels() {
  return useQuery({
    queryKey: agentQueryKey("models"),
    queryFn: async () =>
      array(await e2eAgentJson("/v1/models"), "agent models").map(
        normalizeModel,
      ),
  });
}

export function useAgentModelVersions(companyId: string, live = false) {
  return useQuery({
    queryKey: agentQueryKey("model-versions", companyId),
    enabled: companyId !== "",
    queryFn: async () =>
      array(
        await e2eAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models`,
        ),
        "agent model versions",
      ).map(normalizeModel),
    refetchInterval: live ? 5_000 : false,
  });
}

export function useAgentModel(companyId: string, versionId: string) {
  return useQuery({
    queryKey: agentQueryKey("model", companyId, versionId),
    enabled: companyId !== "" && versionId !== "",
    queryFn: async () =>
      normalizeModel(
        await e2eAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${versionId}`,
        ),
      ),
  });
}

export function useAgentChatThreads(
  companyId: string,
  versionId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: agentQueryKey("chat-threads", companyId, versionId),
    enabled: enabled && companyId !== "" && versionId !== "",
    queryFn: async () =>
      array(
        await e2eAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/chat-threads?limit=50`,
        ),
        "model chat threads",
      ).map(normalizeAgentChatThreadListItem),
  });
}

export function useAgentChatThread(threadId: string) {
  return useQuery({
    queryKey: agentQueryKey("chat-thread", threadId),
    enabled: threadId !== "",
    queryFn: async () =>
      normalizeAgentChatThread(
        await e2eAgentJson(`/v1/chat-threads/${encodeURIComponent(threadId)}`),
      ),
    refetchInterval: (query) =>
      query.state.data?.turns.some(
        (turn) => turn.state === "queued" || turn.state === "running",
      )
        ? 2_000
        : false,
    refetchIntervalInBackground: true,
  });
}

export function useCreateAgentChatThread() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      versionId,
    }: {
      companyId: string;
      versionId: string;
    }) =>
      normalizeAgentChatThread(
        await postE2EAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/chat-threads`,
          {},
        ),
      ),
    onSuccess: async (thread, variables) => {
      client.setQueryData(agentQueryKey("chat-thread", thread.id), thread);
      await client.invalidateQueries({
        queryKey: agentQueryKey(
          "chat-threads",
          variables.companyId,
          variables.versionId,
        ),
      });
    },
  });
}

export function useCreateAgentChatTurn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      message,
      mode,
      editSurface = "web",
      excelSessionKey,
    }: {
      threadId: string;
      message: string;
      mode: AgentChatTurnMode;
      editSurface?: AgentChatEditSurface;
      excelSessionKey?: string;
    }) =>
      normalizeChatTurn(
        await postE2EAgentJson(
          `/v1/chat-threads/${encodeURIComponent(threadId)}/turns`,
          {
            message,
            mode,
            edit_surface: editSurface,
            excel_session_key: excelSessionKey,
          },
          { "Idempotency-Key": crypto.randomUUID() },
        ),
      ),
    onSuccess: async (turn) => {
      client.setQueryData<AgentChatThread>(
        agentQueryKey("chat-thread", turn.thread_id),
        (current) => {
          if (!current) return current;
          const turns = current.turns.filter((item) => item.id !== turn.id);
          turns.push(turn);
          turns.sort((left, right) => left.sequence - right.sequence);
          return { ...current, turns };
        },
      );
      await Promise.all([
        client.invalidateQueries({
          queryKey: agentQueryKey("chat-thread", turn.thread_id),
        }),
        client.invalidateQueries({
          queryKey: agentQueryKey("chat-threads"),
        }),
      ]);
    },
  });
}

function updateChatProposal(
  client: QueryClient,
  proposal: AgentChatEditProposal,
): void {
  client.setQueriesData<AgentChatThread>(
    { queryKey: agentQueryKey("chat-thread") },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        turns: current.turns.map((turn) => {
          if (turn.id !== proposal.turn_id) return turn;
          const currentProposals: AgentChatEditProposal[] | undefined =
            turn.edit_proposals;
          const editProposals = [
            ...(currentProposals ??
              (turn.edit_proposal ? [turn.edit_proposal] : [])),
          ];
          const existingIndex = editProposals.findIndex(
            (item) => item.id === proposal.id,
          );
          if (existingIndex === -1) editProposals.push(proposal);
          else editProposals[existingIndex] = proposal;
          return {
            ...turn,
            edit_proposals: editProposals,
            edit_proposal: editProposals[editProposals.length - 1] ?? null,
          };
        }),
      };
    },
  );
}

function updateChatTurn(client: QueryClient, updated: AgentChatTurn): void {
  client.setQueriesData<AgentChatThread>(
    { queryKey: agentQueryKey("chat-thread") },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        turns: current.turns.map((turn) =>
          turn.id === updated.id ? updated : turn,
        ),
      };
    },
  );
}

async function refreshChatEditedModel(
  client: QueryClient,
  companyId: string,
  versionId: string,
): Promise<void> {
  await Promise.all([
    client.invalidateQueries({
      queryKey: agentQueryKey("xlm", companyId, versionId),
      exact: true,
    }),
    client.invalidateQueries({
      queryKey: agentQueryKey("xlm-revisions", companyId, versionId),
      exact: true,
    }),
    client.invalidateQueries({
      queryKey: agentQueryKey("model", companyId, versionId),
      exact: true,
    }),
    client.invalidateQueries({
      queryKey: agentQueryKey("chat-threads", companyId, versionId),
      exact: true,
    }),
  ]);
}

interface AgentChatEditSynchronization {
  proposal: AgentChatEditProposal;
  resultSnapshot: AgentChatSnapshot | null;
}

interface AgentChatEditApprovalVariables {
  proposalId: string;
  companyId: string;
  versionId: string;
  baseRevision: number;
  operations: XLMEditOperation[];
}

function applyOptimisticChatEdits(
  client: QueryClient,
  companyId: string,
  versionId: string,
  updates: readonly AgentChatEditSynchronization[],
): void {
  client.setQueryData<XLMWorkbookSnapshot>(
    agentQueryKey("xlm", companyId, versionId),
    (cached) => {
      if (!cached) return cached;
      let current = cached;
      for (const { proposal, resultSnapshot } of updates) {
        if (
          proposal.status !== "applied" ||
          proposal.applied_revision === null ||
          current.xlm_snapshot.revision !== proposal.base_revision
        ) {
          continue;
        }
        const projected = applyOptimisticXLMEdits(current, proposal.operations);
        if (!projected) continue;
        current =
          resultSnapshot?.revision === proposal.applied_revision
            ? { ...projected, xlm_snapshot: resultSnapshot }
            : projected;
      }
      return current;
    },
  );
}

export function useApproveAgentChatEditProposal() {
  const client = useQueryClient();
  return useMutation({
    onMutate: async (variables: AgentChatEditApprovalVariables) => {
      const queryKey = agentQueryKey(
        "xlm",
        variables.companyId,
        variables.versionId,
      );
      const previous = client.getQueryData<XLMWorkbookSnapshot>(queryKey);
      const optimistic =
        previous?.xlm_snapshot.revision === variables.baseRevision
          ? applyOptimisticXLMEdits(previous, variables.operations)
          : null;
      const cancellation = cancelXLMSynchronization(
        client,
        variables,
        optimistic === null,
      );
      if (optimistic) client.setQueryData(queryKey, optimistic);
      await cancellation;
      return { previous, queryKey, optimistic: optimistic !== null };
    },
    mutationFn: async ({ proposalId }: AgentChatEditApprovalVariables) =>
      normalizeChatEditDecision(
        await postE2EAgentJson(
          `/v1/chat-edit-proposals/${encodeURIComponent(proposalId)}/approve`,
          {},
        ),
      ),
    onSuccess: async (decision, variables, context) => {
      if (decision.mutation) {
        updateXLMRevisionList(client, variables, decision.mutation);
        await applyXLMMutation(client, variables, decision.mutation);
      } else if (
        decision.proposal.status !== "applied" &&
        context?.optimistic &&
        context.previous
      ) {
        client.setQueryData(context.queryKey, context.previous);
      }
      updateChatProposal(client, decision.proposal);
      await client.invalidateQueries({
        queryKey: agentQueryKey("chat-thread"),
      });
    },
    onError: async (_error, _variables, context) => {
      if (context?.optimistic && context.previous) {
        client.setQueryData(context.queryKey, context.previous);
      }
      await client.invalidateQueries({
        queryKey: agentQueryKey("chat-thread"),
      });
    },
  });
}

export function useRejectAgentChatEditProposal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId }: { proposalId: string }) =>
      normalizeChatEditRejection(
        await postE2EAgentJson(
          `/v1/chat-edit-proposals/${encodeURIComponent(proposalId)}/reject`,
          {},
        ),
      ),
    onSuccess: async (rejection) => {
      updateChatTurn(client, rejection.turn);
      await client.invalidateQueries({
        queryKey: agentQueryKey("chat-thread"),
      });
    },
  });
}

export function useRefreshAgentModelAfterChatEdit() {
  const client = useQueryClient();
  return useCallback(
    async (
      companyId: string,
      versionId: string,
      updates: readonly AgentChatEditSynchronization[],
    ) => {
      const variables = { companyId, versionId };
      const cancellation = cancelXLMSynchronization(client, variables, false);
      applyOptimisticChatEdits(client, companyId, versionId, updates);
      await cancellation;
      await refreshChatEditedModel(client, companyId, versionId);
    },
    [client],
  );
}

export function useXLMModel(
  companyId: string,
  versionId: string,
  syncOnWindowFocus = true,
) {
  const queryClient = useQueryClient();
  const queryKey = agentQueryKey("xlm", companyId, versionId);
  return useQuery({
    queryKey,
    enabled: companyId !== "" && versionId !== "",
    refetchOnWindowFocus: syncOnWindowFocus ? "always" : false,
    queryFn: async ({ signal }) => {
      const cached = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
      if (cached) {
        try {
          const delta = normalizeXLMRevisionDelta(
            await e2eAgentJson(
              `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/delta?base_revision=${cached.xlm_snapshot.revision}`,
              signal,
            ),
          );
          const patched = applyXLMDelta(cached, delta);
          if (patched) return patched;
        } catch (error) {
          if (signal.aborted) throw error;
          // A full verified snapshot remains the compatibility fallback.
        }
      }
      return fetchXLMWorkbookSnapshot(companyId, versionId, signal);
    },
  });
}

export function useXLMRevisions(
  companyId: string,
  versionId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: agentQueryKey("xlm-revisions", companyId, versionId),
    enabled: enabled && companyId !== "" && versionId !== "",
    queryFn: async () =>
      normalizeXLMRevisionList(
        await e2eAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/revisions`,
        ),
      ),
  });
}

export function useXLMRevision(
  companyId: string,
  versionId: string,
  revision: number | null,
) {
  return useQuery({
    queryKey: agentQueryKey("xlm-revision", companyId, versionId, revision),
    enabled: companyId !== "" && versionId !== "" && revision !== null,
    queryFn: async () => {
      if (revision === null) throw new Error("XLM revision is required.");
      return normalizeXLMRevision(
        await e2eAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/revisions/${revision}`,
        ),
      );
    },
  });
}

export function useModelAnnotations(companyId: string, versionId: string) {
  return useQuery({
    queryKey: agentQueryKey("model-annotations", companyId, versionId),
    enabled: companyId !== "" && versionId !== "",
    queryFn: async () =>
      record(
        await e2eAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/annotations`,
        ),
        "model annotations",
      ) as unknown as ModelAnnotationState,
  });
}

export function useCreateAnnotationRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      versionId,
      baseRevision,
      cellUpdates = [],
      modelNote,
    }: {
      companyId: string;
      versionId: string;
      baseRevision: number;
      cellUpdates?: {
        cell: XLMCellAddress;
        document_references: XLMDocumentReference[];
        comment: string | null;
      }[];
      modelNote?: string | null;
    }) =>
      record(
        await postE2EAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/annotation-revisions`,
          {
            base_revision: baseRevision,
            cell_updates: cellUpdates,
            ...(modelNote !== undefined ? { model_note: modelNote } : {}),
          },
        ),
        "model annotation revision",
      ) as unknown as ModelAnnotationState,
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: agentQueryKey(
            "xlm",
            variables.companyId,
            variables.versionId,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: agentQueryKey(
            "model-annotations",
            variables.companyId,
            variables.versionId,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: agentQueryKey(
            "model",
            variables.companyId,
            variables.versionId,
          ),
        }),
      ]);
    },
  });
}

interface XLMCacheMutationVariables {
  companyId: string;
  versionId: string;
}

function mutationSummary(mutation: XLMRevisionMutation): XLMRevisionSummary {
  return {
    revision: mutation.revision,
    xlm_snapshot: mutation.xlm_snapshot,
    kind: mutation.kind,
    changes: mutation.changes,
    finding_count: mutation.finding_count,
    model_note: mutation.model_note,
    restored_from_revision: mutation.restored_from_revision,
    created_at: mutation.created_at,
    created_by: mutation.created_by,
  };
}

export function updateXLMRevisionList(
  queryClient: QueryClient,
  variables: XLMCacheMutationVariables,
  mutation: XLMRevisionMutation,
): void {
  queryClient.setQueryData<XLMRevisionList>(
    agentQueryKey("xlm-revisions", variables.companyId, variables.versionId),
    (current) => {
      if (!current) return current;
      const revisions = [
        mutationSummary(mutation),
        ...current.revisions.filter(
          (revision) => revision.revision !== mutation.revision,
        ),
      ]
        .sort((left, right) => right.revision - left.revision)
        .slice(0, 100);
      return {
        head_revision: Math.max(current.head_revision, mutation.revision),
        revisions,
      };
    },
  );
}

export function applyXLMMutation(
  queryClient: QueryClient,
  variables: XLMCacheMutationVariables,
  mutation: XLMRevisionMutation,
): Promise<void> | void {
  const queryKey = agentQueryKey(
    "xlm",
    variables.companyId,
    variables.versionId,
  );
  let synchronized = false;
  queryClient.setQueryData<XLMWorkbookSnapshot>(queryKey, (current) => {
    if (!current) return current;
    if (current.xlm_snapshot.revision >= mutation.revision) {
      synchronized = true;
      return current;
    }
    if (
      mutation.reload_required ||
      current.xlm_snapshot.revision + 1 !== mutation.revision
    ) {
      return current;
    }
    const model = applyXLMCellPatches(current.model, mutation.cell_patches);
    if (!model) return current;
    synchronized = true;
    return { model, xlm_snapshot: mutation.xlm_snapshot };
  });
  if (synchronized) return;
  return queryClient.invalidateQueries({ queryKey, exact: true });
}

async function cancelXLMSynchronization(
  queryClient: QueryClient,
  variables: XLMCacheMutationVariables,
  revert = true,
): Promise<void> {
  await Promise.all([
    queryClient.cancelQueries(
      {
        queryKey: agentQueryKey(
          "xlm",
          variables.companyId,
          variables.versionId,
        ),
        exact: true,
      },
      { revert },
    ),
    queryClient.cancelQueries(
      {
        queryKey: agentQueryKey(
          "xlm-revisions",
          variables.companyId,
          variables.versionId,
        ),
        exact: true,
      },
      { revert },
    ),
  ]);
}

export function useCreateXLMRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    onMutate: async (variables) => {
      const queryKey = agentQueryKey(
        "xlm",
        variables.companyId,
        variables.versionId,
      );
      const previous = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
      const optimistic =
        variables.optimisticSnapshot !== undefined
          ? variables.optimisticSnapshot
          : variables.optimistic === false
            ? null
            : previous
              ? applyOptimisticXLMEdits(previous, variables.operations)
              : null;
      const cancellation = cancelXLMSynchronization(
        queryClient,
        variables,
        optimistic === null,
      );
      if (optimistic) queryClient.setQueryData(queryKey, optimistic);
      await cancellation;
      return { previous, queryKey, optimistic: optimistic !== null };
    },
    mutationFn: async ({
      companyId,
      versionId,
      baseRevision,
      operations,
      idempotencyKey,
      modelNote,
    }: {
      companyId: string;
      versionId: string;
      baseRevision: number;
      operations: XLMEditOperation[];
      idempotencyKey: string;
      modelNote?: string | null;
      optimistic?: boolean;
      optimisticSnapshot?: XLMWorkbookSnapshot;
    }) =>
      normalizeXLMRevisionMutation(
        await postE2EAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/revisions`,
          {
            base_revision: baseRevision,
            operations,
            ...(modelNote !== undefined ? { model_note: modelNote } : {}),
          },
          { "Idempotency-Key": idempotencyKey },
        ),
      ),
    onSuccess: (data, variables) => {
      updateXLMRevisionList(queryClient, variables, data);
      return applyXLMMutation(queryClient, variables, data);
    },
    onError: (_error, _variables, context) => {
      if (context?.optimistic && context.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
  });
}

export function useRestoreXLMRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    onMutate: async (variables) => {
      const queryKey = agentQueryKey(
        "xlm",
        variables.companyId,
        variables.versionId,
      );
      const previous = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
      const cancellation = cancelXLMSynchronization(
        queryClient,
        variables,
        variables.optimisticSnapshot === undefined,
      );
      if (variables.optimisticSnapshot) {
        queryClient.setQueryData(queryKey, variables.optimisticSnapshot);
      }
      await cancellation;
      return {
        previous,
        queryKey,
        optimistic: variables.optimisticSnapshot !== undefined,
      };
    },
    mutationFn: async ({
      companyId,
      versionId,
      revision,
      baseRevision,
      idempotencyKey,
    }: {
      companyId: string;
      versionId: string;
      revision: number;
      baseRevision: number;
      idempotencyKey: string;
      optimisticSnapshot?: XLMWorkbookSnapshot;
    }) =>
      normalizeXLMRevisionMutation(
        await postE2EAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/revisions/${revision}/restore`,
          { base_revision: baseRevision },
          { "Idempotency-Key": idempotencyKey },
        ),
      ),
    onSuccess: (data, variables) => {
      updateXLMRevisionList(queryClient, variables, data);
      return applyXLMMutation(queryClient, variables, data);
    },
    onError: (_error, _variables, context) => {
      if (context?.optimistic && context.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
  });
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

async function uploadAgentInput(
  companyId: string,
  file: File,
  role: AgentInput["role"],
  metadata: AgentInputMetadata = {},
): Promise<AgentInput> {
  const digest = await sha256(file);
  const created = record(
    await postE2EAgentJson("/v1/inputs", {
      company_id: companyId,
      filename: file.name,
      media_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      sha256: digest,
      role,
      source_url: metadata.sourceUrl?.trim() || null,
      description: metadata.description?.trim() || null,
      release_date: metadata.releaseDate || null,
    }),
    "agent input",
  ) as unknown as AgentInput;
  if (!created.upload_url) throw new Error("Input upload URL is unavailable.");
  await putE2EAgentUpload(created.upload_url, created.upload_headers, file);
  return record(
    await postE2EAgentJson(`/v1/inputs/${created.id}/complete`),
    "completed agent input",
  ) as unknown as AgentInput;
}

export function useUploadAgentInput() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      file,
      role,
      metadata,
    }: {
      companyId: string;
      file: File;
      role: AgentInput["role"];
      metadata?: AgentInputMetadata;
    }) => uploadAgentInput(companyId, file, role, metadata),
    onSuccess: async (_, { companyId }) => {
      await client.invalidateQueries({
        queryKey: agentQueryKey("documents", companyId),
      });
    },
  });
}

export function useCreateAgentCompany() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: AgentCompanyRequest) =>
      normalizeCompany(await postE2EAgentJson("/v1/companies", body)),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: agentQueryKey("companies") });
    },
  });
}

export function useCreateAgentRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: AgentRunRequest) =>
      normalizeAgentRun(
        await postE2EAgentJson("/v1/runs", body, {
          "Idempotency-Key": crypto.randomUUID(),
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: agentQueryKey("runs") });
    },
  });
}

async function invalidateAgentRunQueries(
  client: QueryClient,
  runIds: readonly string[],
): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: agentQueryKey("runs") }),
    ...runIds.map((runId) =>
      client.invalidateQueries({
        queryKey: agentQueryKey("run", runId),
      }),
    ),
  ]);
}

async function invalidateAgentRunVisibilityQueries(
  client: QueryClient,
  runIds: readonly string[],
): Promise<void> {
  await Promise.all([
    invalidateAgentRunQueries(client, runIds),
    client.invalidateQueries({ queryKey: agentQueryKey("companies") }),
    client.invalidateQueries({ queryKey: agentQueryKey("models") }),
    client.invalidateQueries({
      queryKey: agentQueryKey("model-versions"),
    }),
    client.invalidateQueries({ queryKey: agentQueryKey("model") }),
  ]);
}

export function useCancelAgentRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) =>
      postE2EAgentJson(`/v1/runs/${encodeURIComponent(runId)}/cancel`),
    onSuccess: async (_, runId) => invalidateAgentRunQueries(client, [runId]),
  });
}

async function settleAgentRunBatch<T>(
  requests: readonly Promise<T>[],
  failureLabel: string,
): Promise<Awaited<T>[]> {
  const results = await Promise.allSettled(requests);
  const failed = results.filter(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (failed.length > 0) {
    const firstReason = failed[0]?.reason;
    const detail =
      firstReason instanceof Error ? ` ${firstReason.message}` : "";
    throw new Error(
      `${failed.length} of ${results.length} runs could not be ${failureLabel}.${detail}`,
    );
  }
  return results
    .filter(
      (result): result is PromiseFulfilledResult<Awaited<T>> =>
        result.status === "fulfilled",
    )
    .map((result) => result.value);
}

export function useCancelAgentRuns() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (runIds: readonly string[]) =>
      settleAgentRunBatch(
        runIds.map((runId) =>
          postE2EAgentJson(`/v1/runs/${encodeURIComponent(runId)}/cancel`),
        ),
        "canceled",
      ),
    onSettled: async (_data, _error, runIds) =>
      invalidateAgentRunQueries(client, runIds),
  });
}

export function useUpdateAgentRunVisibility() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runId,
      visibility,
    }: {
      runId: string;
      visibility: AgentVisibility;
    }) =>
      normalizeAgentRun(
        await patchE2EAgentJson(
          `/v1/runs/${encodeURIComponent(runId)}/visibility`,
          { visibility },
        ),
      ),
    onSuccess: async (run) => {
      client.setQueryData(agentQueryKey("run", run.id), run);
      await invalidateAgentRunVisibilityQueries(client, [run.id]);
    },
  });
}

export function useUpdateAgentRunsVisibility() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runIds,
      visibility,
    }: {
      runIds: readonly string[];
      visibility: AgentVisibility;
    }) =>
      settleAgentRunBatch(
        runIds.map(async (runId) =>
          normalizeAgentRun(
            await patchE2EAgentJson(
              `/v1/runs/${encodeURIComponent(runId)}/visibility`,
              { visibility },
            ),
          ),
        ),
        `made ${visibility}`,
      ),
    onSuccess: (runs) => {
      for (const run of runs) {
        client.setQueryData(agentQueryKey("run", run.id), run);
      }
    },
    onSettled: async (_data, _error, { runIds }) =>
      invalidateAgentRunVisibilityQueries(client, runIds),
  });
}

export function useUpdateAgentRunDraft() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runId,
      hypothesis,
      iterationMessage,
      parameters,
      agentVersion,
      baseModelVersionId,
    }: {
      runId: string;
      hypothesis: string;
      iterationMessage?: string | null;
      parameters?: AgentRunParameters;
      agentVersion?: string | null;
      baseModelVersionId?: string | null;
    }) =>
      normalizeAgentRun(
        await patchE2EAgentJson(`/v1/runs/${runId}`, {
          hypothesis,
          ...(iterationMessage !== undefined
            ? { iteration_message: iterationMessage }
            : {}),
          ...(parameters ? { parameters } : {}),
          ...(agentVersion !== undefined
            ? { agent_version: agentVersion }
            : {}),
          ...(baseModelVersionId !== undefined
            ? { base_model_version_id: baseModelVersionId }
            : {}),
        }),
      ),
    onSuccess: async (run) => {
      client.setQueryData(agentQueryKey("run", run.id), run);
      await client.invalidateQueries({ queryKey: agentQueryKey("runs") });
    },
  });
}

export function useAttachAgentRunDraftInput() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runId,
      inputId,
    }: {
      runId: string;
      inputId: string;
    }) =>
      normalizeAgentRun(
        await postE2EAgentJson(`/v1/runs/${runId}/inputs/${inputId}`),
      ),
    onSuccess: async (run) => {
      client.setQueryData(agentQueryKey("run", run.id), run);
      await client.invalidateQueries({ queryKey: agentQueryKey("runs") });
    },
  });
}

export function useDetachAgentRunDraftInput() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runId,
      inputId,
    }: {
      runId: string;
      inputId: string;
    }) =>
      normalizeAgentRun(
        await deleteE2EAgentJson(`/v1/runs/${runId}/inputs/${inputId}`),
      ),
    onSuccess: async (run) => {
      client.setQueryData(agentQueryKey("run", run.id), run);
      await client.invalidateQueries({ queryKey: agentQueryKey("runs") });
    },
  });
}

export function useStartAgentRunDraft() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) =>
      normalizeAgentRun(await postE2EAgentJson(`/v1/runs/${runId}/start`)),
    onSuccess: async (run) => {
      client.setQueryData(agentQueryKey("run", run.id), run);
      await client.invalidateQueries({ queryKey: agentQueryKey("runs") });
    },
  });
}

async function invalidateDeletedAgentRuns(
  client: QueryClient,
  runIds: readonly string[],
): Promise<void> {
  for (const runId of runIds) {
    client.removeQueries({ queryKey: agentQueryKey("run", runId) });
  }
  await Promise.all([
    invalidateAgentRunVisibilityQueries(client, []),
    client.invalidateQueries({ queryKey: ["e2e-agent-admin"] }),
  ]);
}

export function useDeleteAgentRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => {
      await deleteE2EAgent(`/v1/runs/${encodeURIComponent(runId)}`);
      return runId;
    },
    onSuccess: (runId) => invalidateDeletedAgentRuns(client, [runId]),
  });
}

export function useDeleteAgentRuns() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runIds: readonly string[]) =>
      Promise.all(
        [...new Set(runIds)].map(async (runId) => {
          try {
            await deleteE2EAgent(`/v1/runs/${encodeURIComponent(runId)}`);
            return { runId, error: null };
          } catch (error) {
            return {
              runId,
              error:
                error instanceof Error ? error.message : "Run deletion failed.",
            };
          }
        }),
      ),
    onSuccess: (results) =>
      invalidateDeletedAgentRuns(
        client,
        results
          .filter((result) => result.error === null)
          .map((result) => result.runId),
      ),
  });
}

export async function agentArtifactLinks(
  runId: string,
  roles: string[],
): Promise<
  { role: string; filename: string; url: string; expires_in_seconds: number }[]
> {
  const value = record(
    await postE2EAgentJson(`/v1/runs/${runId}/artifact-links`, { roles }),
    "artifact links",
  );
  return array(value.links, "artifact links").map((raw) => {
    const item = record(raw, "artifact link");
    const role = requiredString(item, "role", "artifact link");
    const filename = requiredString(item, "filename", "artifact link");
    const url = requiredString(item, "url", "artifact link");
    if (typeof item.expires_in_seconds !== "number") {
      throw new Error("Invalid artifact link expiry.");
    }
    return { role, filename, url, expires_in_seconds: item.expires_in_seconds };
  });
}

export function modelSummaryContent(
  companyId: string,
  versionId: string,
): Promise<Response> {
  return e2eAgentResponse(
    `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/summary`,
  );
}

export function useAgentArtifactLinks() {
  return useMutation({
    mutationFn: ({ runId, roles }: { runId: string; roles: string[] }) =>
      agentArtifactLinks(runId, roles),
  });
}

export function useAgentDocumentLink() {
  return useMutation({
    mutationFn: async ({
      companyId,
      documentId,
    }: {
      companyId: string;
      documentId: string;
    }) => {
      const item = record(
        await postE2EAgentJson(
          `/v1/companies/${encodeURIComponent(companyId)}/documents/${encodeURIComponent(documentId)}/link`,
        ),
        "agent document link",
      );
      return {
        id: requiredString(item, "id", "agent document link"),
        filename: requiredString(item, "filename", "agent document link"),
        url: requiredString(item, "url", "agent document link"),
        preview_token:
          typeof item.preview_token === "string" ? item.preview_token : null,
        expires_in_seconds: requiredNumber(
          item,
          "expires_in_seconds",
          "agent document link",
        ),
      };
    },
  });
}
